function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.dispatchProductSelectEvent();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getAllSelectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
      if (checked) {
        options.push({ name: checked.dataset.optionName || '', value: checked.value });
      }
    });
    return options;
  }

  dispatchProductSelectEvent() {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent) return;

    const deferred = ProductSelectEvent.createPromise();
    this.pendingSelectPromise = deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred = this.pendingSelectPromise;
    this.pendingSelectPromise = null;
    return deferred;
  }

  resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
    const deferred = this.takePendingSelectPromise();
    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: variant.available,
          price: {
            amount: sourceVariantSelects?.dataset.selectedPriceAmount,
            currencyCode: sourceVariantSelects?.dataset.currencyCode,
          },
          selectedOptions: this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({ variant: null });
    }
  }

  rejectPendingSelectPromise(error) {
    this.takePendingSelectPromise()?.reject(error);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  getCartQuantityForLine(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    return parseInt(input?.dataset.cartQuantity || input?.getAttribute('value') || '0', 10) || 0;
  }

  startCartLinesUpdate(items) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    const linesByAction = Object.entries(items).reduce((groups, [variantId, quantity]) => {
      const nextQuantity = parseInt(quantity, 10);
      const currentQuantity = this.getCartQuantityForLine(variantId);

      if (Number.isNaN(nextQuantity) || currentQuantity === nextQuantity) return groups;

      const action = currentQuantity === 0 ? 'add' : nextQuantity === 0 ? 'remove' : 'update';
      let line;
      if (action === 'add') {
        line = { merchandiseId: variantId, quantity: nextQuantity };
      } else {
        const lineKey = this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.dataset.quantityLineKey;
        // No AJAX line key on the row — likely cached HTML rendered before this
        // attribute landed. Skip rather than emit an event with id: ''.
        if (!lineKey) return groups;
        line = { id: lineKey, quantity: nextQuantity };
      }

      if (!groups[action]) groups[action] = [];
      groups[action].push(line);
      return groups;
    }, {});

    const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'product',
          lines,
          promise: deferred.promise,
        })
      );
      return deferred;
    });

    return {
      resolve: (parsedState) => {
        const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
        deferreds.forEach((deferred) => deferred.resolve(payload));
      },
      reject: (error) => {
        deferreds.forEach((deferred) => deferred.reject(error));
      },
    };
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;

    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


// TwiniPaws desktop navigation: open dropdowns on hover.
document.addEventListener('DOMContentLoaded', () => {
  if (!window.matchMedia('(min-width: 990px)').matches) return;

  document.querySelectorAll('.header__inline-menu details').forEach((details) => {
    let closeTimer;
    const summary = details.querySelector('summary');

    details.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      details.setAttribute('open', '');
      summary?.setAttribute('aria-expanded', 'true');
    });

    details.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(() => {
        details.removeAttribute('open');
        summary?.setAttribute('aria-expanded', 'false');
      }, 140);
    });
  });
});


// TwiniPaws homepage three-step image carousel.
document.addEventListener('DOMContentLoaded', () => {
  const heading = [...document.querySelectorAll('.twp-wrap section > h2')].find(
    (item) => item.textContent.trim().toLowerCase() === 'make it yours in three steps.'
  );
  if (!heading || heading.parentElement.classList.contains('twp-image-carousel')) return;

  const section = heading.parentElement;
  section.classList.add('twp-image-carousel');
  heading.textContent = 'Make It Yours in Three Steps.';

  const images = [
    ['https://cdn.shopify.com/s/files/1/0773/8317/2315/files/twini-blue-collar.jpg?v=1786655154', 'Blue TwiniPaws dog collar'],
    ['https://twinipaws.com/cdn/shop/files/DSC_1107_71ee6d59-c79d-4100-9ade-c28cd8dba613.jpg?v=1786557847&width=1200', 'TwiniPaws dog charm'],
    ['https://twinipaws.com/cdn/shop/files/ChatGPTImageMar10_2026at03_47_33PM.png?v=1786557752&width=1200', 'Human and dog matching set'],
    ['https://twinipaws.com/cdn/shop/files/BlackCollar1.jpg?v=1786557716&width=1200', 'TwiniPaws black collection'],
    ['https://twinipaws.com/cdn/shop/files/DSC_1176.jpg?v=1786557665&width=1200', 'TwiniPaws red collection'],
    ['https://twinipaws.com/cdn/shop/files/DSC_1533.jpg?v=1786557505&width=1200', 'TwiniPaws yellow collection']
  ];

  section.querySelector('.twp-steps')?.remove();
  const viewport = document.createElement('div');
  viewport.className = 'twp-carousel-viewport';
  viewport.setAttribute('aria-label', 'TwiniPaws product gallery');
  const track = document.createElement('div');
  track.className = 'twp-carousel-track';
  track.innerHTML = images.map(([src, alt]) => '<figure class="twp-carousel-slide"><img src="' + src + '" alt="' + alt + '" loading="lazy"></figure>').join('');
  viewport.appendChild(track);
  section.appendChild(viewport);

  let current = 0;
  let timer;
  const advance = () => {
    const slides = [...track.children];
    if (!slides.length) return;
    current = (current + 1) % slides.length;
    track.scrollTo({ left: slides[current].offsetLeft - track.offsetLeft, behavior: 'smooth' });
  };
  const start = () => { clearInterval(timer); timer = setInterval(advance, 3800); };
  const stop = () => clearInterval(timer);
  viewport.addEventListener('pointerenter', stop);
  viewport.addEventListener('pointerleave', start);
  viewport.addEventListener('touchstart', stop, { passive: true });
  viewport.addEventListener('touchend', start, { passive: true });
  start();
});


// TwiniPaws continuous, seamless carousel motion.
document.addEventListener('DOMContentLoaded', () => {
  requestAnimationFrame(() => {
    const viewport = document.querySelector('.twp-carousel-viewport');
    const track = document.querySelector('.twp-carousel-track');

    if (!viewport || !track || track.dataset.continuousReady) return;

    track.dataset.continuousReady = 'true';

    // Stop the previous timed slide change and prevent it restarting.
    viewport.dispatchEvent(new Event('pointerenter'));
    track.scrollTo = () => {};

    const originalSlides = [...track.children];
    originalSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });

    let lastFrame = performance.now();
    let touching = false;
    const speed = 0.04;

    const move = (now) => {
      const elapsed = Math.min(now - lastFrame, 50);
      lastFrame = now;

      if (!touching) {
        track.scrollLeft += elapsed * speed;
        const loopPoint = track.scrollWidth / 2;

        if (track.scrollLeft >= loopPoint) {
          track.scrollLeft -= loopPoint;
        }
      }

      requestAnimationFrame(move);
    };

    viewport.addEventListener('touchstart', () => {
      touching = true;
    }, { passive: true });

    viewport.addEventListener('touchend', () => {
      touching = false;
      lastFrame = performance.now();
    }, { passive: true });

    requestAnimationFrame(move);
  });
});


// TwiniPaws continuous, seamless carousel motion.
document.addEventListener('DOMContentLoaded', () => {
  requestAnimationFrame(() => {
    const viewport = document.querySelector('.twp-carousel-viewport');
    const track = document.querySelector('.twp-carousel-track');
    if (!viewport || !track || track.dataset.continuousReady) return;

    track.dataset.continuousReady = 'true';
    viewport.dispatchEvent(new Event('pointerenter'));
    track.scrollTo = () => {};

    const originalSlides = [...track.children];
    originalSlides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });

    let lastFrame = performance.now();
    let touching = false;
    const speed = 0.04;

    const move = (now) => {
      const elapsed = Math.min(now - lastFrame, 50);
      lastFrame = now;
      if (!touching) {
        track.scrollLeft += elapsed * speed;
        const loopPoint = track.scrollWidth / 2;
        if (track.scrollLeft >= loopPoint) track.scrollLeft -= loopPoint;
      }
      requestAnimationFrame(move);
    };

    viewport.addEventListener('touchstart', () => { touching = true; }, { passive: true });
    viewport.addEventListener('touchend', () => {
      touching = false;
      lastFrame = performance.now();
    }, { passive: true });

    requestAnimationFrame(move);
  });
});

// TwiniPaws enhanced product cards and product information.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.card-wrapper').forEach(async (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    const media = card.querySelector('.card__media .media');
    if (!link || !media || media.dataset.twiniEnhanced) return;
    media.dataset.twiniEnhanced = 'true';

    try {
      const url = new URL(link.href, location.origin);
      const response = await fetch(url.pathname + '.js');
      if (!response.ok) return;
      const product = await response.json();
      if (!product.images || product.images.length < 2) return;

      const image = document.createElement('img');
      image.className = 'motion-reduce twp-card-secondary';
      image.src = product.images[1];
      image.alt = product.title + ' lifestyle view';
      image.loading = 'lazy';
      image.width = 900;
      image.height = 900;
      media.appendChild(image);
      card.classList.add('twp-has-secondary');
    } catch (error) {}
  });

  const description = document.querySelector('.product__description.rte');
  if (!description || description.dataset.twiniAccordion) return;
  description.dataset.twiniAccordion = 'true';

  const originalContent = description.innerHTML;
  const sections = [
    ['Product Details', originalContent],
    ['Materials', '<p>Made with durable, comfortable materials selected for everyday use.</p>'],
    ['Sizing', '<p>Please review the size options above and choose the best fit before ordering.</p>'],
    ['Care', '<p>Gently wipe clean and allow to air dry completely.</p>'],
    ['Shipping & Returns', '<p>Orders are carefully packed and shipped from the U.S. Please review measurements before ordering.</p>']
  ];

  const wrapper = document.createElement('div');
  wrapper.className = 'twp-product-accordions';

  sections.forEach((section, index) => {
    const item = document.createElement('details');
    item.className = 'twp-product-accordion';
    item.open = index === 0;

    const summary = document.createElement('summary');
    summary.textContent = section[0];

    const body = document.createElement('div');
    body.className = 'twp-product-accordion__content';
    body.innerHTML = section[1];

    item.append(summary, body);
    wrapper.appendChild(item);
  });

  description.replaceChildren(wrapper);
  document.querySelectorAll('.product__media-list > li:nth-child(n+7)').forEach((item) => {
    item.classList.add('twp-extra-product-media');
  });
});

// TwiniPaws Build Your Match configurator.
document.addEventListener('DOMContentLoaded', async () => {
  const charmGroups = {"Spring":["Tent","Cupcake","Pine Tree","Mushroom","Fox"],"Summer":["Wave","Octopus","Flower","Butterfly","Ice Cream"],"Autumn":["Hot Dog","Acorn","Pumpkin","Deer","Owl"],"Winter":["Snowflake","Scarf","Snowman","Gingerbread","Tomato Soup"],"Perfect Duo":["You Woof Good","Pride Heart","Love Heart","Key","Shell"]};
  const charmImage = '/cdn/shop/files/for-pup-charms-1.png';
  const charmNames = Object.entries(charmGroups).flatMap(([season,names]) => names.map((name) => ({season,name})));

  const charmPicker = (onChoose, compact=false) => {
    const wrap=document.createElement('div');
    wrap.className='twp-charm-library'+(compact?' twp-charm-library--compact':'');
    wrap.innerHTML='<div class="twp-charm-reference"><img src="'+charmImage+'" alt="TwiniPaws matching charm collection — 25 designs for pups and people"></div><div class="twp-charm-picker"><div class="twp-charm-filters"></div><div class="twp-charm-choices"></div></div>';
    const filters=['All',...Object.keys(charmGroups)];
    const filterBox=wrap.querySelector('.twp-charm-filters');
    const choices=wrap.querySelector('.twp-charm-choices');
    const draw=(filter)=>{
      choices.replaceChildren();
      charmNames.filter(item=>filter==='All'||item.season===filter).forEach(item=>{
        const button=document.createElement('button');
        button.type='button'; button.className='twp-charm-choice';
        button.dataset.design=item.name;
        const index=charmNames.findIndex(charm=>charm.name===item.name); const col=index%5; const row=Math.floor(index/5); const cropX=[53,107,163,219,276][col]; const cropY=[113,173,231,292,352][row]; button.innerHTML='<span class="twp-charm-copy"><span>'+item.name+'</span><small>'+item.season+'</small></span><span class="twp-charm-thumb" aria-hidden="true"><img src="/cdn/shop/files/'+slug(item.name)+'.png" alt=""></span>';
        button.addEventListener('click',()=>{
          wrap.querySelectorAll('.twp-charm-choice').forEach(b=>b.classList.toggle('is-selected',b===button));
          onChoose(item,button);
        });
        choices.appendChild(button);
      });
    };
    filters.forEach((name,index)=>{
      const button=document.createElement('button');
      button.type='button'; button.textContent=name;
      if(index===0) button.className='is-active';
      button.addEventListener('click',()=>{
        filterBox.querySelectorAll('button').forEach(b=>b.classList.toggle('is-active',b===button));
        draw(name);
      });
      filterBox.appendChild(button);
    });
    draw('All');
    return wrap;
  };

  if (['/collections/dog-essentials','/collections/human-jewelry'].includes(location.pathname)) {
    const target=document.querySelector('.collection');
    if(target&&!document.querySelector('.twp-collection-charms')){
      const section=document.createElement('section');
      section.className='twp-collection-charms page-width';
      section.innerHTML='<div class="twp-collection-charms__head"><p class="twp-match-eyebrow">25 MATCHING DESIGNS</p><h2>Charms for every little story</h2><p>Every design is available for pups and people. Pick a favorite, then build your match.</p></div>';
      const picker=charmPicker((item)=>{
        location.href='/collections/matching-sets?charm='+encodeURIComponent(item.name);
      },true);
      section.appendChild(picker);
      target.appendChild(section);
    }
    return;
  }

  if (location.pathname !== '/collections/matching-sets') return;
  const main = document.querySelector('#MainContent');
  if (!main || document.querySelector('.twp-match-builder')) return;

  const catalog = {
    pup: [['/products/dog-collar','Collar'],['/products/dog-harness','Harness'],['/products/dog-leash','Leash']],
    human: [['/products/twinipaws-purple-collection','Bracelet'],['/products/twinipaws-black-collection','Necklace']]
  };
  const state = { mode:'both', pup:null, human:null, charm:null };
  const money = (value) => '$' + (value / 100).toFixed(2);

  const builder = document.createElement('div');
  builder.className = 'twp-match-builder';
  builder.innerHTML = '<section class="twp-match-hero page-width"><p class="twp-match-eyebrow">MADE TO MATCH</p><h1>Build Your Match</h1><p>Choose a piece for your pup, one for you, and charms that tell the same little story.</p><div class="twp-match-modes"><button class="is-active" data-mode="both">Pup + You</button><button data-mode="pup">For Pup</button><button data-mode="human">For You</button></div></section><section class="twp-match-workspace page-width"><div class="twp-match-steps"><div class="twp-match-step" data-section="pup"><div class="twp-match-step__head"><span>01</span><div><h2>Pick your pup piece</h2><p>Start with a collar, harness, or leash.</p></div></div><div class="twp-match-grid" data-grid="pup"></div></div><div class="twp-match-step" data-section="human"><div class="twp-match-step__head"><span>02</span><div><h2>Pick your piece</h2><p>Choose something you will love wearing too.</p></div></div><div class="twp-match-grid" data-grid="human"></div></div><div class="twp-match-step" data-section="charm"><div class="twp-match-step__head"><span>03</span><div><h2>Choose matching charms</h2><p>Pick one of 25 designs. In Pup + You mode, you receive the matching pair.</p></div></div><div data-grid="charm"></div></div></div><aside class="twp-match-summary"><p class="twp-match-eyebrow">YOUR MATCH</p><h2>Made by you</h2><div class="twp-match-summary__items"></div><div class="twp-match-total"><span>Total</span><strong>$0.00</strong></div><button class="button twp-match-add" disabled>Add Match to Cart</button><p class="twp-match-note">Select your pieces to complete the match.</p></aside></section>';
  main.replaceChildren(builder);

  const getProduct = async (entry) => {
    try {
      const response = await fetch(entry[0] + '.js');
      if (!response.ok) return null;
      const data = await response.json();
      const variants = data.variants.filter((variant) => variant.available);
      if (!variants.length) return null;
      return { path:entry[0], title:entry[1], image:data.images[0], price:data.price, variants };
    } catch (error) { return null; }
  };

  const choose = (group, product, id) => {
    const variant = product.variants.find((item) => String(item.id) === String(id)) || product.variants[0];
    state[group] = { path:product.path, title:product.title, image:product.image, variant };
    builder.querySelectorAll('[data-group="' + group + '"]').forEach((card) => card.classList.toggle('is-selected', card.dataset.path === product.path));
    updateSummary();
  };

  const makeCard = (product, group) => {
    const card = document.createElement('article');
    card.className = 'twp-match-card';
    card.dataset.group = group;
    card.dataset.path = product.path;
    card.innerHTML = '<img src="' + product.image + '" alt="' + product.title + '"><div class="twp-match-card__info"><div><h3>' + product.title + '</h3><p>From ' + money(product.price) + '</p></div></div><span class="twp-match-check">✓</span>';
    if (product.variants.length > 1) {
      const select = document.createElement('select');
      select.className = 'twp-match-variant';
      select.setAttribute('aria-label','Choose options for ' + product.title);
      product.variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title + ' — ' + money(variant.price);
        select.appendChild(option);
      });
      select.addEventListener('click',(event)=>event.stopPropagation());
      select.addEventListener('change',()=>{ if(state[group] && state[group].path===product.path) choose(group,product,select.value); });
      card.querySelector('.twp-match-card__info').appendChild(select);
    }
    card.addEventListener('click',()=>{ const select=card.querySelector('select'); choose(group,product,select?select.value:product.variants[0].id); });
    return card;
  };

  const charmHandle = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,'') + '-matching-charm';
  const charmVariantName = () => state.mode === 'both' ? 'Matching Pair' : state.mode === 'pup' ? 'For Pup' : 'For You';
  const applyCharmVariant = () => {
    if (!state.charm || !state.charm.product) return;
    state.charm.variant = state.charm.product.variants.find((variant) => variant.title === charmVariantName()) || state.charm.product.variants[0];
  };
  const picker=charmPicker(async(item)=>{
    const product=await getProduct(['/products/'+charmHandle(item.name),item.name+' Matching Charm']);
    if(!product) return;
    state.charm={path:product.path,title:item.name+' Charm',design:item.name,season:item.season,image:charmImage,variant:product.variants[0],product};
    applyCharmVariant();
    updateSummary();
  });
  builder.querySelector('[data-grid="charm"]').appendChild(picker);

  const requested=new URLSearchParams(location.search).get('charm');
  if(requested){
    const requestedButton=[...picker.querySelectorAll('.twp-charm-choice')].find(b=>b.dataset.design===requested);
    if(requestedButton) requestedButton.click();
  }

  const activeGroups = () => state.mode === 'both' ? ['pup','human','charm'] : [state.mode,'charm'];
  const updateSummary = () => {
    const box = builder.querySelector('.twp-match-summary__items');
    box.replaceChildren();
    let total = 0;
    activeGroups().forEach((group) => {
      const selected = state[group];
      const row = document.createElement('div');
      row.className = 'twp-match-summary__row';
      if (selected) {
        const quantity = 1;
        total += selected.variant.price * quantity;
        row.innerHTML = '<img src="' + selected.image + '" alt=""><div><span>' + (group==='pup'?'For pup':group==='human'?'For you':'Matching charm') + '</span><strong>' + selected.title + (quantity>1?' × 2':'') + '</strong><small>' + (group==='charm'?selected.season:selected.variant.title) + '</small></div>';
      } else {
        row.innerHTML = '<div class="twp-match-summary__empty">Choose ' + (group==='pup'?'a pup piece':group==='human'?'your piece':'your charms') + '</div>';
      }
      box.appendChild(row);
    });
    const ready = activeGroups().every((group)=>state[group]);
    builder.querySelector('.twp-match-total strong').textContent = money(total);
    builder.querySelector('.twp-match-add').disabled = !ready;
    builder.querySelector('.twp-match-note').textContent = ready ? 'Your match is ready.' : 'Select your pieces to complete the match.';
  };

  builder.querySelectorAll('[data-mode]').forEach((button)=>button.addEventListener('click',()=>{
    state.mode=button.dataset.mode;
    applyCharmVariant();
    builder.querySelectorAll('[data-mode]').forEach((item)=>item.classList.toggle('is-active',item===button));
    builder.querySelector('[data-section="pup"]').hidden=state.mode==='human';
    builder.querySelector('[data-section="human"]').hidden=state.mode==='pup';
    updateSummary();
  }));

  for (const group of Object.keys(catalog)) {
    const grid=builder.querySelector('[data-grid="' + group + '"]');
    const products=await Promise.all(catalog[group].map(getProduct));
    products.filter(Boolean).forEach((product)=>grid.appendChild(makeCard(product,group)));
  }

  builder.querySelector('.twp-match-add').addEventListener('click',async()=>{
    const button=builder.querySelector('.twp-match-add');
    const items=activeGroups().map((group)=>({id:state[group].variant.id,quantity:1,properties:group==='charm'?{'Built as':'TwiniPaws Match','Charm design':state.charm.design,'Charm collection':state.charm.season}:{'Built as':'TwiniPaws Match'}}));
    button.disabled=true; button.textContent='Adding your match...';
    try {
      const response=await fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items})});
      if(!response.ok) throw new Error();
      location.href='/cart';
    } catch(error) { button.disabled=false; button.textContent='Try Again'; }
  });
  updateSummary();
});

// TwiniPaws individual charm product images.
document.addEventListener('DOMContentLoaded', () => {
  const names=['Tent','Cupcake','Pine Tree','Mushroom','Fox','Wave','Octopus','Flower','Butterfly','Ice Cream','Hot Dog','Acorn','Pumpkin','Deer','Owl','Snowflake','Scarf','Snowman','Gingerbread','Tomato Soup','You Woof Good','Pride Heart','Love Heart','Key','Shell'];
  const x=[6.74,13.69,20.86,27.95,35.25], y=[27.19,41.78,55.70,70.29,84.88];
  const copies={"Tent":"A little home under the stars.","Cupcake":"You make every day worth celebrating.","Pine Tree":"Let’s take the long way today.","Mushroom":"Look what we found along the way!","Fox":"Wherever you wander, I’m coming too.","Wave":"One more wave before we go?","Octopus":"If I had eight arms, they’d all be hugs for you.","Flower":"I sniffed them all—this one’s for you! ★","Butterfly":"Wait—let’s watch it fly.","Ice Cream":"One more lick, please? ★","Hot Dog":"Of course I saved you the best bite.","Acorn":"A little treasure from the way home.","Pumpkin":"Already found the cutest one in the patch.","Deer":"Did you see it too?!","Owl":"Some cuddles are worth staying up for.","Snowflake":"First one out in the fresh snow.","Scarf":"Cold days are better wrapped up together.","Snowman":"Can we stay out a little longer?","Gingerbread":"Something sweet for someone sweeter.","Tomato Soup":"Something warm, someone close.","You Woof Good":"Every day is better by your side.","Pride Heart":"Love looks good on everyone.","Love Heart":"I love you a billion million trillion.","Key":"<p><strong>For Pup:</strong> You’ll always have the key to my heart.</p><p><strong>For You:</strong> You unlocked a whole new world in me.</p>","Shell":"<p><strong>For Pup:</strong> What if mermaids had paws?</p><p><strong>For You:</strong> Then I’ll be your shell.</p>"};
  const slug=(value)=>value.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,'');
  const byHandle=new Map(names.map((name,index)=>[slug(name)+'-matching-charm',{name,index}]));
  const style=document.createElement('style');
  style.textContent='.twp-charm-artwork{background-image:none!important;background-size:1880% 1042.5%!important;background-position:var(--twp-charm-x) var(--twp-charm-y)!important;background-repeat:no-repeat!important;background-color:#faf6ed!important}.twp-charm-artwork img{opacity:1!important;object-fit:contain!important}';
  document.head.appendChild(style);
  const apply=(element,item)=>{if(!element)return;element.classList.add('twp-charm-artwork');element.style.setProperty('--twp-charm-x',x[item.index%5]+'%');element.style.setProperty('--twp-charm-y',y[Math.floor(item.index/5)]+'%');};
  const decorate=()=>{
    document.querySelectorAll('a[href*="/products/"]').forEach((link)=>{
      const match=(link.getAttribute('href')||'').match(/\/products\/([^?#/]+)/);if(!match)return;
      const item=byHandle.get(match[1]);if(!item)return;
      const card=link.closest('.card-wrapper,.grid__item');if(card)card.querySelectorAll('.card__media .media,.card__media').forEach((media)=>apply(media,item));
    });
    const handle=location.pathname.match(/^\/products\/([^/]+)/)?.[1];const item=byHandle.get(handle);
    if(item){
      document.querySelectorAll('.product__media,.product-media-container').forEach((media)=>apply(media,item));
      const description=document.querySelector('.product__description');
      if(description&&copies[item.name]&&description.dataset.twpCharmDescription!==item.name){description.dataset.twpCharmDescription=item.name;description.innerHTML=copies[item.name].startsWith('<p>')?copies[item.name]:'<p>'+copies[item.name]+'</p>';}
    }
  };
  decorate();new MutationObserver(decorate).observe(document.querySelector('#MainContent')||document.body,{childList:true,subtree:true});
});


// TwiniPaws newsletter-to-announcement update.
(function () {
  var announcementCopy = 'Get 10% off your first order, plus early access to new charms, drops, and Twini favorites.';

  function updateTwiniPromo() {
    document.querySelectorAll('h1, h2, h3, p, div, span').forEach(function (element) {
      var text = (element.textContent || '').trim();

      if (text === 'A little something for your first match.') {
        var wronglyHidden = element.closest('.shopify-section');
        if (wronglyHidden) {
          wronglyHidden.style.removeProperty('display');
          wronglyHidden.removeAttribute('aria-hidden');
        }
        var section = element.closest('section') || element.parentElement;
        if (section) {
          section.style.setProperty('display', 'none', 'important');
          section.setAttribute('aria-hidden', 'true');
        }
      }

      if (text === 'Made to match. Made for your bond.' && element.matches('p, span')) {
        element.textContent = announcementCopy;
        element.style.setProperty('display', 'block', 'important');
        element.style.setProperty('width', '100%', 'important');
        element.style.setProperty('text-align', 'center', 'important');
        if (element.parentElement) element.parentElement.style.setProperty('text-align', 'center', 'important');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateTwiniPromo);
  } else {
    updateTwiniPromo();
  }

  new MutationObserver(updateTwiniPromo).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();


// TwiniPaws newsletter above custom footer.
(function () {
  function placeNewsletterAboveFooter() {
    var customFooter = document.querySelector('footer.tpf');
    var newsletter = document.querySelector('.footer-block__newsletter');
    var nativeFooter = document.querySelector('footer.footer');
    if (!customFooter || !newsletter) return;

    var section = document.querySelector('.twp-footer-newsletter');
    if (!section) {
      section = document.createElement('section');
      section.className = 'twp-footer-newsletter';
      section.style.cssText = 'background:transparent;padding:54px 24px 44px;text-align:center;display:flex;justify-content:center;align-items:center;color:#fff;';
      var inner = document.createElement('div');
      inner.style.cssText = 'width:min(100%,540px);margin:0 auto;text-align:center;';
      section.appendChild(inner);
      customFooter.insertBefore(section, customFooter.firstChild);
    }

    var target = section.firstElementChild;
    if (newsletter.parentElement !== target) target.appendChild(newsletter);
    newsletter.style.cssText = 'width:100%;max-width:540px;margin:0 auto;text-align:center;';
    var heading = newsletter.querySelector('.footer-block__heading');
    if (heading) heading.style.textAlign = 'center';
    var form = newsletter.querySelector('form');
    if (form) {
      form.style.width = '100%';
      form.style.margin = '0 auto';
    }
    var style = document.getElementById('twp-footer-newsletter-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'twp-footer-newsletter-style';
      style.textContent = '.twp-footer-newsletter .footer-block__heading{color:#fff!important}.twp-footer-newsletter .field:after{box-shadow:0 0 0 1px rgba(255,255,255,.75)!important}.twp-footer-newsletter .field__input,.twp-footer-newsletter .field__label,.twp-footer-newsletter .newsletter-form__button{color:#fff!important}.twp-footer-newsletter .field__input{background:transparent!important}';
      document.head.appendChild(style);
    }
    if (nativeFooter) nativeFooter.style.setProperty('display', 'none', 'important');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', placeNewsletterAboveFooter);
  else placeNewsletterAboveFooter();
  new MutationObserver(placeNewsletterAboveFooter).observe(document.documentElement, { childList: true, subtree: true });
})();


// TwiniPaws replaceable editorial hero.
(function () {
  var heroImage = 'https://cdn.shopify.com/s/files/1/0773/8317/2315/files/twinipaws-hero-match-v1.png?v=1787843121';
  function applyTwiniHero() {
    var hero = document.querySelector('.twp-hero');
    if (!hero) return;
    hero.style.setProperty('background-image', 'linear-gradient(90deg, rgba(250,246,237,.98) 0%, rgba(250,246,237,.88) 30%, rgba(250,246,237,.18) 58%, rgba(250,246,237,0) 72%), url("' + heroImage + '")', 'important');
    hero.style.setProperty('background-size', 'cover', 'important');
    hero.style.setProperty('background-position', 'center, center', 'important');
    hero.style.setProperty('background-repeat', 'no-repeat', 'important');
    hero.style.setProperty('min-height', '620px', 'important');
    hero.style.setProperty('padding', '72px 68px', 'important');
    hero.style.setProperty('display', 'flex', 'important');
    hero.style.setProperty('align-items', 'center', 'important');
    hero.style.setProperty('overflow', 'hidden', 'important');
    var copy = hero.querySelector('.twp-hero-copy');
    if (copy) {
      copy.style.setProperty('max-width', '500px', 'important');
      copy.style.setProperty('position', 'relative', 'important');
      copy.style.setProperty('z-index', '2', 'important');
    }
    var oldImage = hero.querySelector('.twp-hero-img');
    if (oldImage) oldImage.style.setProperty('display', 'none', 'important');
    var style = document.getElementById('twp-editorial-hero-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'twp-editorial-hero-style';
      style.textContent = '@media(max-width:749px){.twp-hero{min-height:640px!important;padding:38px 24px!important;align-items:flex-start!important;background-image:linear-gradient(180deg,rgba(250,246,237,.98) 0%,rgba(250,246,237,.88) 34%,rgba(250,246,237,.10) 62%,rgba(250,246,237,0) 78%),url("' + heroImage + '")!important;background-position:center,62% center!important}.twp-hero-copy{max-width:100%!important}.twp-actions{gap:10px!important;flex-wrap:wrap!important}}';
      document.head.appendChild(style);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyTwiniHero);
  else applyTwiniHero();
  new MutationObserver(applyTwiniHero).observe(document.documentElement, { childList: true, subtree: true });
})();


// TwiniPaws transparent larger header logo.
(function () {
  function styleTwiniPawsLogo() {
    document.querySelectorAll('.header__heading-logo').forEach(function (logo) {
      logo.style.setProperty('mix-blend-mode', 'multiply', 'important');
      logo.style.setProperty('width', window.matchMedia('(max-width: 749px)').matches ? '100px' : '105px', 'important');
      logo.style.setProperty('max-width', 'none', 'important');
      logo.style.setProperty('height', 'auto', 'important');
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', styleTwiniPawsLogo);
  else styleTwiniPawsLogo();
  window.addEventListener('resize', styleTwiniPawsLogo);
  new MutationObserver(styleTwiniPawsLogo).observe(document.documentElement, { childList: true, subtree: true });
})();


// TwiniPaws remove matching charms reference image.
(function () {
  function removeMatchingReferenceImage() {
    document.querySelectorAll('.twp-charm-reference').forEach(function (el) { el.remove(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', removeMatchingReferenceImage);
  else removeMatchingReferenceImage();
  new MutationObserver(removeMatchingReferenceImage).observe(document.documentElement, { childList: true, subtree: true });
})();


// TwiniPaws polished charm picker layout.
(function(){function applyCharmPickerLayout(){if(document.getElementById('twp-polished-charm-picker'))return;var style=document.createElement('style');style.id='twp-polished-charm-picker';style.textContent=".twp-charm-library{display:block!important;width:100%!important}\n.twp-charm-picker{width:100%!important;max-width:none!important}\n.twp-charm-filters{display:grid!important;grid-template-columns:repeat(6,minmax(0,1fr))!important;gap:10px!important;margin:24px 0 28px!important}\n.twp-charm-filters button{width:100%!important;min-width:0!important;height:50px!important;padding:8px 10px!important;white-space:nowrap!important}\n.twp-charm-choices{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:14px!important;width:100%!important}\n.twp-charm-choice{width:100%!important;height:110px!important;min-width:0!important;padding:12px!important;grid-template-columns:72px minmax(0,1fr)!important;gap:12px!important;align-items:center!important;border-radius:18px!important;overflow:hidden!important}\n.twp-charm-thumb{width:72px!important;height:72px!important;border-radius:14px!important;flex:none!important}\n.twp-charm-copy{min-width:0!important;text-align:left!important;display:flex!important;flex-direction:column!important;gap:6px!important}\n.twp-charm-copy>span{font-size:16px!important;line-height:1.25!important;overflow-wrap:anywhere!important}\n.twp-charm-copy small{font-size:13px!important;line-height:1.2!important}\n@media(max-width:989px){.twp-charm-filters{grid-template-columns:repeat(3,minmax(0,1fr))!important}.twp-charm-choices{grid-template-columns:repeat(3,minmax(0,1fr))!important}.twp-charm-choice{grid-template-columns:64px minmax(0,1fr)!important;height:100px!important}.twp-charm-thumb{width:64px!important;height:64px!important}}\n@media(max-width:749px){.twp-charm-filters{gap:8px!important;margin:18px 0 22px!important}.twp-charm-filters button{height:44px!important;font-size:13px!important}.twp-charm-choices{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:10px!important}.twp-charm-choice{grid-template-columns:56px minmax(0,1fr)!important;height:92px!important;padding:10px!important;gap:9px!important;border-radius:16px!important}.twp-charm-thumb{width:56px!important;height:56px!important}.twp-charm-copy>span{font-size:14px!important}.twp-charm-copy small{font-size:11px!important}}";document.head.appendChild(style)}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',applyCharmPickerLayout);else applyCharmPickerLayout()})();


// TwiniPaws charm sprite crop correction.
(function(){if(document.getElementById('twp-charm-sprite-crop'))return;var s=document.createElement('style');s.id='twp-charm-sprite-crop';s.textContent=".twp-charm-choice{height:96px!important;grid-template-columns:52px minmax(0,1fr)!important}.twp-charm-thumb{width:48px!important;height:48px!important;border-radius:12px!important}@media(max-width:749px){.twp-charm-choice{height:86px!important;grid-template-columns:48px minmax(0,1fr)!important}.twp-charm-thumb{width:48px!important;height:48px!important}}";document.head.appendChild(s)})();


// TwiniPaws homepage brief restructure.
(function(){function rebuildHome(){if(location.pathname!=='/'||document.body.dataset.twpHomeBrief==='done')return;var root=document.querySelector('.twp');if(!root)return;document.body.dataset.twpHomeBrief='done';var hero=root.querySelector('.twp-hero');var first=hero&&hero.nextElementSibling;if(first&&first.querySelector('h2')&&/Made to match your moments/i.test(first.querySelector('h2').textContent))first.remove();var steps=root.querySelector('.twp-image-carousel');if(steps){steps.className='twp-home-steps';steps.innerHTML="<div id=\"twp-made-to-match\" aria-labelledby=\"twp-made-to-match-title\"><header class=\"twp-match-intro-head\"><h2 id=\"twp-made-to-match-title\">Made to Match. Made to Be Yours.</h2><p>Matching accessories for you and your pup, with playful charms that make every piece uniquely yours.</p></header><div class=\"twp-match-intro-grid\"><article class=\"twp-match-intro-card\"><img src=\"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/twini-blue-collar.jpg?v=1786655154\" alt=\"TwiniPaws collar and charms for your pup\" loading=\"lazy\"><div class=\"twp-match-intro-copy\"><h3>For Your Pup</h3><p>Everyday essentials with a little extra personality.</p></div></article><article class=\"twp-match-intro-card\"><img src=\"https://twinipaws.com/cdn/shop/files/ChatGPTImageMar10_2026at03_47_33PM.png?v=1786557752&amp;width=1200\" alt=\"A person and their pup wearing TwiniPaws accessories\" loading=\"lazy\"><div class=\"twp-match-intro-copy\"><h3>For You</h3><p>Playful pieces to keep your pup close, wherever you go.</p></div></article><article class=\"twp-match-intro-card\"><img src=\"https://twinipaws.com/cdn/shop/files/DSC_1107_71ee6d59-c79d-4100-9ade-c28cd8dba613.jpg?v=1786557847&amp;width=1200\" alt=\"A pup wearing colorful TwiniPaws charms\" loading=\"lazy\"><div class=\"twp-match-intro-copy\"><h3>Your Little Details</h3><p>Mix and match charms to make your pieces your own.</p></div></article></div><div class=\"twp-match-intro-actions\"><a class=\"twp-match-intro-cta\" href=\"/collections/matching-sets\">Customize Your Match<span class=\"twp-match-intro-arrow\" aria-hidden=\"true\"><svg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" class=\"icon icon-arrow\" viewBox=\"0 0 14 10\"><path fill=\"currentColor\" fill-rule=\"evenodd\" d=\"M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546\" clip-rule=\"evenodd\"></path></svg></span></a></div></div>"}var band=[].slice.call(root.querySelectorAll('.twp-band')).find(function(x){return /Find your favorite/i.test(x.textContent)});if(band){band.className='twp-home-products';band.innerHTML="<h2>Find Your Favorite</h2><p class=\"twp-section-intro\">Shop the pieces made for pups, people, and every match between.</p><div class=\"twp-category-grid\"><a class=\"twp-category-card\" href=\"/products/dog-collar\"><img src=\"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/twini-blue-collar.jpg?v=1786655154\" alt=\"Collar\"><span>Collar</span></a><a class=\"twp-category-card\" href=\"/products/dog-harness\"><img src=\"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/BlueHarnessJPG_82c1a464-cb67-470b-a253-938dc4891114.jpg?v=1786557905\" alt=\"Harness\"><span>Harness</span></a><a class=\"twp-category-card\" href=\"/products/dog-leash\"><img src=\"https://twinipaws.com/cdn/shop/files/BlackCollar1.jpg?v=1786557716&width=900\" alt=\"Leash\"><span>Leash</span></a><a class=\"twp-category-card\" href=\"/products/dog-poop-bag-holder\"><img src=\"https://twinipaws.com/cdn/shop/files/DSC_1176.jpg?v=1786557665&width=900\" alt=\"Poop Bag Holder\"><span>Poop Bag Holder</span></a><a class=\"twp-category-card twp-category-card--human-base\" href=\"/collections/human-jewelry?view=twp-bracelets\"><img src=\"https://twinipaws.com/cdn/shop/t/4/assets/twp-human-bracelet-gold-20261103-v2.png\" alt=\"Bracelet\"><span>Bracelet</span></a><a class=\"twp-category-card twp-category-card--human-base\" href=\"/collections/human-jewelry?view=twp-necklaces\"><img src=\"https://twinipaws.com/cdn/shop/t/4/assets/twp-human-necklace-gold-20261103-v2.png\" alt=\"Necklace\"><span>Necklace</span></a><a class=\"twp-category-card\" href=\"/collections/human-jewelry\"><img src=\"https://twinipaws.com/cdn/shop/files/for-pup-charms-1_6388d25c-5d5b-4ffa-a374-84294ed44cd1.png?v=1787691429&width=900\" alt=\"Human Charms\"><span>Human Charms</span></a><a class=\"twp-category-card\" href=\"/collections/dog-charms\"><img src=\"https://twinipaws.com/cdn/shop/files/DSC_1107_71ee6d59-c79d-4100-9ade-c28cd8dba613.jpg?v=1786557847&width=900\" alt=\"Dog Charms\"><span>Dog Charms</span></a></div>"}var story=root.querySelector('.twp-story');if(story){story.classList.remove('twp-blue');var t=story.querySelector('h2');if(t)t.textContent='More Than Matching'}}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',rebuildHome);else rebuildHome()})();
(function(){if(document.getElementById('twp-home-brief-style'))return;var s=document.createElement('style');s.id='twp-home-brief-style';s.textContent=".twp-home-steps{margin:0 0 54px}\n.twp-home-steps>h2,.twp-home-products>h2{text-align:center;margin-bottom:10px!important}\n.twp-section-intro{text-align:center;max-width:680px;margin:0 auto 28px}\n.twp-step-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}\n.twp-step-card{background:#fff;border:1px solid rgba(89,66,57,.14);border-radius:22px;overflow:hidden;display:flex;flex-direction:column}\n.twp-step-card img{display:block;width:100%;height:250px;object-fit:cover}\n.twp-step-copy{padding:22px 22px 24px;display:flex;flex-direction:column;align-items:flex-start;flex:1}\n.twp-step-number{font-size:13px;letter-spacing:.12em;text-transform:uppercase;color:#8d7b72;margin-bottom:8px}\n.twp-step-copy h3{margin:0 0 8px}\n.twp-step-copy p{margin:0 0 18px}\n.twp-step-copy .twp-btn{margin-top:auto}\n.twp-home-products{margin:0 0 54px}\n.twp-category-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-top:26px}\n.twp-category-card{position:relative;display:block;min-height:250px;border-radius:20px;overflow:hidden;text-decoration:none;color:#fff;background:#ddd}\n.twp-category-card img{width:100%;height:250px;display:block;object-fit:cover;transition:transform .35s ease}\n.twp-category-card:after{content:'';position:absolute;inset:35% 0 0;background:linear-gradient(transparent,rgba(36,25,20,.72))}\n.twp-category-card span{position:absolute;z-index:2;left:18px;right:18px;bottom:17px;color:#fff;font-size:20px;line-height:1.2}\n.twp-category-card:hover img{transform:scale(1.035)}\n.twp-home-products .twp-category-card--human-base{background:#fff;outline:1px solid rgba(89,66,57,.12);outline-offset:-1px}\n.twp-home-products .twp-category-card--human-base img{object-fit:contain;box-sizing:border-box;padding:12px 12px 44px;background:#fff}\n.twp-home-products .twp-category-card--human-base:after{display:none}\n.twp-home-products .twp-category-card--human-base span{color:#594239}\n.twp-story:not(.twp-blue){background:#fff!important;border:1px solid rgba(89,66,57,.12)}\n@media(max-width:989px){.twp-category-grid{grid-template-columns:repeat(2,1fr)}}\n@media(max-width:749px){.twp-step-grid{grid-template-columns:1fr}.twp-step-card img{height:230px}.twp-category-grid{grid-template-columns:repeat(2,1fr);gap:10px}.twp-category-card,.twp-category-card img{height:180px;min-height:180px}.twp-category-card span{font-size:16px;left:12px;bottom:12px}}";document.head.appendChild(s)})();


// TwiniPaws stable three-step homepage block.
(function(){function stableSteps(){if(location.pathname!=='/')return;var old=document.querySelector('.twp-image-carousel');if(!old)return;old.style.setProperty('display','none','important');if(!document.querySelector('.twp-home-steps'))old.insertAdjacentHTML('beforebegin',"<section class=\"twp-home-steps\"><div id=\"twp-made-to-match\" aria-labelledby=\"twp-made-to-match-title\"><header class=\"twp-match-intro-head\"><h2 id=\"twp-made-to-match-title\">Made to Match. Made to Be Yours.</h2><p>Matching accessories for you and your pup, with playful charms that make every piece uniquely yours.</p></header><div class=\"twp-match-intro-grid\"><article class=\"twp-match-intro-card\"><img src=\"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/twini-blue-collar.jpg?v=1786655154\" alt=\"TwiniPaws collar and charms for your pup\" loading=\"lazy\"><div class=\"twp-match-intro-copy\"><h3>For Your Pup</h3><p>Everyday essentials with a little extra personality.</p></div></article><article class=\"twp-match-intro-card\"><img src=\"https://twinipaws.com/cdn/shop/files/ChatGPTImageMar10_2026at03_47_33PM.png?v=1786557752&amp;width=1200\" alt=\"A person and their pup wearing TwiniPaws accessories\" loading=\"lazy\"><div class=\"twp-match-intro-copy\"><h3>For You</h3><p>Playful pieces to keep your pup close, wherever you go.</p></div></article><article class=\"twp-match-intro-card\"><img src=\"https://twinipaws.com/cdn/shop/files/DSC_1107_71ee6d59-c79d-4100-9ade-c28cd8dba613.jpg?v=1786557847&amp;width=1200\" alt=\"A pup wearing colorful TwiniPaws charms\" loading=\"lazy\"><div class=\"twp-match-intro-copy\"><h3>Your Little Details</h3><p>Mix and match charms to make your pieces your own.</p></div></article></div><div class=\"twp-match-intro-actions\"><a class=\"twp-match-intro-cta\" href=\"/collections/matching-sets\">Customize Your Match<span class=\"twp-match-intro-arrow\" aria-hidden=\"true\"><svg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" class=\"icon icon-arrow\" viewBox=\"0 0 14 10\"><path fill=\"currentColor\" fill-rule=\"evenodd\" d=\"M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546\" clip-rule=\"evenodd\"></path></svg></span></a></div></div></section>")}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',stableSteps);else stableSteps();new MutationObserver(stableSteps).observe(document.documentElement,{childList:true,subtree:true})})();





// TwiniPaws official Instagram link.
(function(){function linkInstagram(){document.querySelectorAll("a").forEach(function(a){var label=(a.textContent||"").trim().toLowerCase();var href=(a.getAttribute("href")||"").toLowerCase();if(label.indexOf("instagram")>=0||href.indexOf("instagram.com")>=0){var target="https://www.instagram.com/twini.paws/";if(a.getAttribute("href")!==target)a.setAttribute("href",target);a.setAttribute("target","_blank");a.setAttribute("rel","noopener noreferrer")}})}if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",linkInstagram);else linkInstagram();new MutationObserver(linkInstagram).observe(document.documentElement,{childList:true,subtree:true})})();


// TwiniPaws compact one-page charm grid with quick add.
(function () {
  var copies = {
    "tent": "A little home under the stars.",
    "cupcake": "You make every day worth celebrating.",
    "pine-tree": "Let’s take the long way today.",
    "mushroom": "Look what we found along the way!",
    "fox": "Wherever you wander, I’m coming too.",
    "wave": "One more wave before we go?",
    "octopus": "If I had eight arms, they’d all be hugs for you.",
    "flower": "I sniffed them all—this one’s for you!",
    "butterfly": "Wait—let’s watch it fly.",
    "ice-cream": "One more lick, please?",
    "hot-dog": "Of course I saved you the best bite.",
    "acorn": "A little treasure from the way home.",
    "pumpkin": "Already found the cutest one in the patch.",
    "deer": "Did you see it too?!",
    "owl": "Some cuddles are worth staying up for.",
    "snowflake": "First one out in the fresh snow.",
    "scarf": "Cold days are better wrapped up together.",
    "snowman": "Can we stay out a little longer?",
    "gingerbread": "Something sweet for someone sweeter.",
    "tomato-soup": "Something warm, someone close.",
    "you-woof-good": "Every day is better by your side.",
    "pride-heart": "Love looks good on everyone.",
    "love-heart": "I love you a billion million trillion.",
    "key": "You’ll always have the key to my heart.",
    "shell": "What if mermaids had paws?"
  };

  function charmSlug(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return "";
    return (link.pathname.split("/products/")[1] || "").replace("-matching-charm", "");
  }

  function isCharmCard(card) {
    return !!copies[charmSlug(card)];
  }

  function updateCartBubble(count) {
    var bubble = document.querySelector(".cart-count-bubble");
    if (!bubble) return;
    bubble.classList.remove("hidden");
    var visibleCount = bubble.querySelector("span[aria-hidden='true']");
    if (visibleCount) visibleCount.textContent = count;
    var accessibleCount = bubble.querySelector("span:not([aria-hidden])");
    if (accessibleCount) accessibleCount.textContent = count + " items";
  }

  async function quickAdd(button, card, mode) {
    if (button.disabled) return;
    button.disabled = true;
    button.classList.add("is-loading");
    var originalLabel = button.getAttribute("aria-label");
    try {
      var link = card.querySelector('a[href*="/products/"]');
      var handle = link.pathname.split("/products/")[1];
      var productResponse = await window.fetch("/products/" + handle + ".js");
      if (!productResponse.ok) throw new Error("Product unavailable");
      var product = await productResponse.json();
      var wanted = mode === "dog-charms" ? "For Pup" : "For You";
      var variant = product.variants.find(function (item) {
        return item.title === wanted || (item.options || []).indexOf(wanted) >= 0;
      });
      if (!variant || !variant.available) throw new Error("Variant unavailable");
      var addResponse = await window.fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
      });
      if (!addResponse.ok) throw new Error("Unable to add");
      var cartResponse = await window.fetch("/cart.js");
      var cart = await cartResponse.json();
      updateCartBubble(cart.item_count);
      button.classList.remove("is-loading");
      button.classList.add("is-added");
      button.setAttribute("aria-label", "Added to cart");
      window.setTimeout(function () {
        button.classList.remove("is-added");
        button.disabled = false;
        button.setAttribute("aria-label", originalLabel);
      }, 1100);
    } catch (error) {
      button.classList.remove("is-loading");
      button.classList.add("is-error");
      button.setAttribute("aria-label", "Could not add to cart");
      window.setTimeout(function () {
        button.classList.remove("is-error");
        button.disabled = false;
        button.setAttribute("aria-label", originalLabel);
      }, 1500);
    }
  }

  function decorateCard(card, mode) {
    if (card.dataset.twpCompactReady === "true" || !isCharmCard(card)) return;
    card.dataset.twpCompactReady = "true";
    var slug = charmSlug(card);
    var title = card.querySelector(".card > .card__content:last-child .card__heading a");
    var info = card.querySelector(".card > .card__content:last-child .card__information");
    var itemName = title ? title.textContent.replace("Matching ", "").trim() : "Charm";
    if (mode === "human-charms" && slug === "you-woof-good") itemName = "You Feel Like Home Charm";
    if (mode === "human-charms" && slug === "key") itemName = "Heart Lock Charm";
    if (title) title.textContent = itemName;
    if (info && !info.querySelector(".twp-compact-copy")) {
      var copy = document.createElement("p");
      copy.className = "twp-compact-copy";
      copy.textContent = copies[slug];
      if (mode === "human-charms" && slug === "key") copy.textContent = "You unlocked a whole new world in me.";
      if (mode === "human-charms" && slug === "shell") copy.textContent = "Then I’ll be your shell.";
      info.appendChild(copy);
    }
    var media = card.querySelector(".card__inner");
    if (media && !media.querySelector(".twp-quick-add")) {
      var button = document.createElement("button");
      button.type = "button";
      button.className = "twp-quick-add";
      button.setAttribute("aria-label", "Add " + itemName + " to cart");
      button.innerHTML = '<span class="twp-plus-icon" aria-hidden="true"><img src="/cdn/shop/t/4/assets/icon-plus.svg" alt=""></span><span class="twp-added-icon" aria-hidden="true">✓</span>';
      button.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        quickAdd(button, card, mode);
      });
      media.appendChild(button);
    }
  }

  async function buildOnePageGrid() {
    document.querySelectorAll("a").forEach(function (anchor) {
      var text = (anchor.textContent || "").trim();
      if (text === "Dog Charms") anchor.setAttribute("href", "/collections/human-jewelry?view=dog-charms");
      if (text === "Human Charms") anchor.setAttribute("href", "/collections/human-jewelry?view=human-charms");
    });
    if (location.pathname === "/collections/dog-charms") {
      location.replace("/collections/human-jewelry?view=dog-charms");
      return;
    }
    if (location.pathname !== "/collections/human-jewelry") return;
    var params = new URLSearchParams(location.search);
    var mode = params.get("view");
    if (mode !== "dog-charms" && mode !== "human-charms") return;
    if (params.get("page") === "2") {
      location.replace("/collections/human-jewelry?view=" + mode);
      return;
    }
    document.body.classList.add("twp-compact-charms-page");
    var heading = document.querySelector("main h1");
    if (heading) heading.textContent = mode === "dog-charms" ? "Dog Charms" : "Human Charms";
    var grid = document.querySelector("#product-grid");
    if (!grid) return;

    try {
      var pageTwoResponse = await window.fetch("/collections/human-jewelry?view=" + mode + "&page=2");
      if (pageTwoResponse.ok) {
        var html = await pageTwoResponse.text();
        var pageTwo = new DOMParser().parseFromString(html, "text/html");
        pageTwo.querySelectorAll("#product-grid > li").forEach(function (item) {
          var card = item.querySelector(".card-wrapper");
          if (!card || !isCharmCard(card)) return;
          var link = card.querySelector('a[href*="/products/"]');
          var exists = link && grid.querySelector('a[href="' + link.getAttribute("href") + '"]');
          if (!exists) grid.appendChild(item);
        });
      }
    } catch (error) {
      console.warn("TwiniPaws: page two charms could not be loaded", error);
    }

    grid.querySelectorAll(":scope > li").forEach(function (item) {
      var card = item.querySelector(".card-wrapper");
      if (!card || !isCharmCard(card)) item.remove();
    });
    grid.querySelectorAll(".card-wrapper").forEach(function (card) {
      decorateCard(card, mode);
    });
    document.querySelectorAll(".pagination-wrapper").forEach(function (pagination) {
      pagination.remove();
    });
    document.querySelectorAll(".product-count__text").forEach(function (count) {
      count.textContent = grid.querySelectorAll(":scope > li").length + " products";
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", buildOnePageGrid);
  } else {
    buildOnePageGrid();
  }
})();

(function () {
  if (document.getElementById("twp-compact-charms-style")) return;
  var style = document.createElement("style");
  style.id = "twp-compact-charms-style";
  style.textContent = [
    ".twp-compact-charms-page #product-grid{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:18px!important}",
    ".twp-compact-charms-page #product-grid>li{width:100%!important;max-width:none!important}",
    ".twp-compact-charms-page .card-wrapper{height:100%}",
    ".twp-compact-charms-page .card{display:grid!important;grid-template-columns:92px minmax(0,1fr)!important;min-height:132px!important;height:100%!important;background:#fff!important;border:1px solid rgba(89,66,57,.14)!important;border-radius:20px!important;padding:16px!important;gap:15px!important;overflow:visible!important}",
    ".twp-compact-charms-page .card__inner{width:92px!important;height:92px!important;min-height:92px!important;border-radius:15px!important;overflow:visible!important;align-self:center!important;position:relative!important}",
    ".twp-compact-charms-page .card__inner:before{display:none!important}",
    ".twp-compact-charms-page .card__media,.twp-compact-charms-page .card__media .media{position:absolute!important;inset:0!important;width:92px!important;height:92px!important;border-radius:15px!important;overflow:hidden!important}",
    ".twp-compact-charms-page .card__media img{width:100%!important;height:100%!important;object-fit:contain!important;position:static!important}",
    ".twp-compact-charms-page .card>.card__content:first-of-type{display:none!important}",
    ".twp-compact-charms-page .card>.card__content:last-child{display:flex!important;align-items:center!important;padding:0!important;min-width:0!important}",
    ".twp-compact-charms-page .card>.card__content:last-child .card__information{padding:0!important;min-width:0!important}",
    ".twp-compact-charms-page .card__heading{font-size:17px!important;line-height:1.25!important;margin:0 0 8px!important}",
    ".twp-compact-charms-page .card-information,.twp-compact-charms-page .price{display:none!important}",
    ".twp-compact-copy{font-size:12px!important;line-height:1.35!important;color:#8b7770!important;margin:0!important;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}",
    ".twp-quick-add{position:absolute!important;z-index:4!important;top:-10px!important;right:-10px!important;width:32px!important;height:32px!important;border-radius:50%!important;border:1px solid #594239!important;background:#fff!important;display:grid!important;place-items:center!important;padding:0!important;cursor:pointer!important;box-shadow:0 3px 10px rgba(89,66,57,.14)!important;transition:transform .18s ease,background .18s ease!important}",
    ".twp-quick-add:hover{transform:scale(1.07)!important;background:#aad4f3!important}",
    ".twp-quick-add img{width:14px!important;height:14px!important;display:block!important}",
    ".twp-added-icon{display:none!important;font-size:16px!important;color:#fff!important}",
    ".twp-quick-add.is-added{background:#594239!important}",
    ".twp-quick-add.is-added .twp-plus-icon{display:none!important}",
    ".twp-quick-add.is-added .twp-added-icon{display:block!important}",
    ".twp-quick-add.is-loading{opacity:.55!important;pointer-events:none!important}",
    ".twp-quick-add.is-error{background:#f4c9c9!important}",
    "@media(max-width:989px){.twp-compact-charms-page #product-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}}",
    "@media(max-width:749px){.twp-compact-charms-page #product-grid{gap:10px!important}.twp-compact-charms-page .card{grid-template-columns:72px minmax(0,1fr)!important;min-height:108px!important;padding:12px!important;gap:11px!important;border-radius:16px!important}.twp-compact-charms-page .card__inner,.twp-compact-charms-page .card__media,.twp-compact-charms-page .card__media .media{width:72px!important;height:72px!important;min-height:72px!important}.twp-compact-charms-page .card__heading{font-size:14px!important;margin-bottom:5px!important}.twp-compact-copy{font-size:10px!important}.twp-quick-add{width:28px!important;height:28px!important;top:-8px!important;right:-8px!important}}"
  ].join("");
  document.head.appendChild(style);
})();



// TwiniPaws quick-add click layer fix.
(function () {
  function liftQuickAddButtons() {
    document.querySelectorAll(".twp-quick-add").forEach(function (button) {
      var card = button.closest(".card");
      if (card && button.parentElement !== card) card.appendChild(button);
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", liftQuickAddButtons);
  } else {
    liftQuickAddButtons();
  }
  new MutationObserver(liftQuickAddButtons).observe(document.documentElement, { childList: true, subtree: true });
})();

(function () {
  if (document.getElementById("twp-quick-add-click-fix")) return;
  var style = document.createElement("style");
  style.id = "twp-quick-add-click-fix";
  style.textContent = ".twp-compact-charms-page .card>.twp-quick-add{top:8px!important;left:92px!important;right:auto!important;z-index:20!important;pointer-events:auto!important}@media(max-width:749px){.twp-compact-charms-page .card>.twp-quick-add{top:6px!important;left:70px!important}}";
  document.head.appendChild(style);
})();



// TwiniPaws charm quantity picker and selected highlight.
(function () {
  function modeName() {
    return new URLSearchParams(location.search).get("view") === "dog-charms" ? "For Pup" : "For You";
  }

  function productHandle(card) {
    var link = card && card.querySelector('a[href*="/products/"]');
    return link ? (link.pathname.split("/products/")[1] || "") : "";
  }

  function updateBubble(count) {
    var bubble = document.querySelector(".cart-count-bubble");
    if (!bubble) return;
    bubble.classList.toggle("hidden", count < 1);
    var visible = bubble.querySelector("span[aria-hidden='true']");
    var accessible = bubble.querySelector("span:not([aria-hidden])");
    if (visible) visible.textContent = count;
    if (accessible) accessible.textContent = count + " items";
  }

  function icon(path, className) {
    return '<img class="' + className + '" src="/cdn/shop/t/4/assets/' + path + '" alt="">';
  }

  function render(button) {
    var quantity = Number(button.dataset.quantity || 0);
    var expanded = button.dataset.expanded === "true";
    var card = button.closest(".card");
    if (card) card.classList.toggle("twp-is-selected", quantity > 0);
    button.classList.toggle("twp-has-quantity", quantity > 0);
    button.classList.toggle("twp-is-expanded", quantity > 0 && expanded);
    button.setAttribute("aria-label", quantity ? quantity + " selected. Click to change quantity" : "Add charm to cart");
    if (!quantity) {
      button.innerHTML = '<span class="twp-add-one" aria-hidden="true">' + icon("icon-plus.svg", "twp-picker-icon") + '</span>';
    } else if (!expanded) {
      button.innerHTML = '<span class="twp-qty-number" aria-hidden="true">' + quantity + '</span>';
    } else {
      button.innerHTML = '<span class="twp-qty-minus" data-action="minus" aria-hidden="true">' + icon("icon-minus.svg", "twp-picker-icon") + '</span><span class="twp-qty-number" data-action="collapse" aria-hidden="true">' + quantity + '</span><span class="twp-qty-plus" data-action="plus" aria-hidden="true">' + icon("icon-plus.svg", "twp-picker-icon") + '</span>';
    }
  }

  async function variantFor(button) {
    if (button.dataset.variantId) return Number(button.dataset.variantId);
    var card = button.closest(".card");
    var handle = productHandle(card);
    var response = await window.fetch("/products/" + handle + ".js");
    if (!response.ok) throw new Error("Product unavailable");
    var product = await response.json();
    var wanted = modeName();
    var variant = product.variants.find(function (item) {
      return item.title === wanted || (item.options || []).indexOf(wanted) >= 0;
    });
    if (!variant || !variant.available) throw new Error("Variant unavailable");
    button.dataset.variantId = variant.id;
    return variant.id;
  }

  async function changeQuantity(button, nextQuantity) {
    button.disabled = true;
    button.classList.add("is-loading");
    try {
      var variantId = await variantFor(button);
      var current = Number(button.dataset.quantity || 0);
      var response;
      if (nextQuantity > current) {
        response = await window.fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ items: [{ id: variantId, quantity: nextQuantity - current }] })
        });
      } else {
        var cartBefore = await (await window.fetch("/cart.js")).json();
        var line = cartBefore.items.find(function (item) { return Number(item.variant_id) === variantId; });
        response = await window.fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify({ id: line ? line.key : variantId, quantity: Math.max(0, nextQuantity) })
        });
      }
      if (!response.ok) throw new Error("Cart update failed");
      var cart = await (await window.fetch("/cart.js")).json();
      var updated = cart.items.find(function (item) { return Number(item.variant_id) === variantId; });
      button.dataset.quantity = updated ? updated.quantity : 0;
      button.dataset.expanded = Number(button.dataset.quantity) > 0 ? "true" : "false";
      updateBubble(cart.item_count);
      render(button);
    } catch (error) {
      button.classList.add("is-error");
      window.setTimeout(function () { button.classList.remove("is-error"); }, 1200);
    } finally {
      button.disabled = false;
      button.classList.remove("is-loading");
    }
  }

  async function handlePickerClick(event) {
    var button = event.target.closest && event.target.closest(".twp-quick-add");
    if (!button || !document.body.classList.contains("twp-compact-charms-page")) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (button.disabled) return;
    var quantity = Number(button.dataset.quantity || 0);
    var expanded = button.dataset.expanded === "true";
    var actionTarget = event.target.closest("[data-action]");
    var action = actionTarget && actionTarget.dataset.action;
    if (!quantity) return changeQuantity(button, 1);
    if (!expanded) {
      button.dataset.expanded = "true";
      render(button);
      return;
    }
    if (action === "minus") return changeQuantity(button, Math.max(0, quantity - 1));
    if (action === "plus") return changeQuantity(button, quantity + 1);
    button.dataset.expanded = "false";
    render(button);
  }

  async function syncPickers() {
    if (!document.body.classList.contains("twp-compact-charms-page")) return;
    var buttons = Array.prototype.slice.call(document.querySelectorAll(".twp-quick-add"));
    if (!buttons.length) return;
    var cart;
    try { cart = await (await window.fetch("/cart.js")).json(); } catch (error) { return; }
    var wanted = modeName();
    buttons.forEach(function (button) {
      if (button.dataset.twpPickerReady === "true") return;
      button.dataset.twpPickerReady = "true";
      var handle = productHandle(button.closest(".card"));
      var item = cart.items.find(function (line) {
        return line.handle === handle && (line.variant_title === wanted || (line.options_with_values || []).some(function (option) { return option.value === wanted; }));
      });
      button.dataset.quantity = item ? item.quantity : 0;
      if (item) button.dataset.variantId = item.variant_id;
      button.dataset.expanded = "false";
      render(button);
    });
    updateBubble(cart.item_count);
  }

  document.addEventListener("click", handlePickerClick, true);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", syncPickers);
  else syncPickers();
  window.setTimeout(syncPickers, 900);
  window.setTimeout(syncPickers, 2200);
  new MutationObserver(function () { if (document.querySelector(".twp-quick-add:not([data-twp-picker-ready=\"true\"])") ) syncPickers(); }).observe(document.documentElement, { childList: true, subtree: true });
})();

(function () {
  if (document.getElementById("twp-charm-quantity-style")) return;
  var style = document.createElement("style");
  style.id = "twp-charm-quantity-style";
  style.textContent = [
    ".twp-compact-charms-page .card>.twp-quick-add{top:12px!important;right:12px!important;left:auto!important;width:34px!important;height:34px!important;z-index:30!important;background:#fff!important;border-color:#594239!important}",
    ".twp-compact-charms-page .card>.twp-quick-add.twp-has-quantity{background:#aad4f3!important;border-color:#7db9e6!important;color:#2f3b43!important;font-weight:700!important}",
    ".twp-compact-charms-page .card>.twp-quick-add.twp-is-expanded{width:102px!important;border-radius:18px!important;display:flex!important;justify-content:space-between!important;padding:0 8px!important}",
    ".twp-qty-minus,.twp-qty-plus,.twp-qty-number{height:32px;min-width:24px;display:grid;place-items:center}",
    ".twp-picker-icon{width:13px!important;height:13px!important;display:block!important}",
    ".twp-compact-charms-page .card.twp-is-selected .card__inner{background:#dff1ff!important;box-shadow:0 0 0 3px #aad4f3!important}",
    ".twp-compact-charms-page .card.twp-is-selected{border-color:#aad4f3!important}",
    ".twp-compact-charms-page .card>.twp-quick-add:hover{background:#dff1ff!important}",
    "@media(max-width:749px){.twp-compact-charms-page .card>.twp-quick-add{top:8px!important;right:8px!important;left:auto!important;width:30px!important;height:30px!important}.twp-compact-charms-page .card>.twp-quick-add.twp-is-expanded{width:88px!important}.twp-qty-minus,.twp-qty-plus,.twp-qty-number{height:28px;min-width:20px}}"
  ].join("");
  document.head.appendChild(style);
})();



/* TwiniPaws — dog-specific charm photography (no lobster clasp). */
(() => {
  const DOG_CHARM_IMAGES = {"tent":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/tent_9f8505fa-5e37-4c33-b7eb-9b1848816c4e.png?v=1788280836","cupcake":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/cupcake_98dfd435-76dc-4d96-89cf-5fe99cda2f75.png?v=1788280838","pine-tree":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/pine-tree_ae759512-2010-4f62-96b0-23f2503b4792.png?v=1788280839","mushroom":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/mushroom_7addd4ec-6a2f-437e-a57f-cd57fabf5719.png?v=1788280836","fox":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/fox_32036b1f-4188-4685-bb6c-24f4ff105a84.png?v=1788280836","wave":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/wave_b0baba37-4ff3-49b0-9c0d-e93f5e5e4d58.png?v=1788280838","octopus":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/octopus_a351adea-1c43-406d-a366-3ffe8a54698e.png?v=1788280838","flower":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/flower_4af0719e-9741-4c4a-9513-791b3ced9af0.png?v=1788280839","butterfly":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/butterfly_65a36cf3-5ffd-4ec6-a04e-5380c118205a.png?v=1788280839","ice-cream":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/ice-cream_c6237779-bfb6-4c82-9791-d94ef07555c2.png?v=1788280838","hot-dog":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/hot-dog_0c47b91d-20fe-45b0-8474-fa70352f708a.png?v=1788280837","acorn":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/acorn_b7ff1190-20b3-4f08-8eb5-f03fd86ee360.png?v=1788280838","pumpkin":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/pumpkin_4c51af9b-feb0-4404-b841-cf43f7175113.png?v=1788280838","deer":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/deer_fd78567d-e4a1-4c3d-840b-30beb4b9ceda.png?v=1788280837","owl":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/owl_86d9eff0-1ac9-4ffd-bea0-00a5f309c490.png?v=1788280838","snowflake":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/snowflake_aaf159dd-38cf-49ab-906e-0436a2c6e83d.png?v=1788280838","scarf":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/scarf_6ce6759f-da5a-4d9b-8700-a014a2520eb0.png?v=1788280836","snowman":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/snowman_54695f68-817c-424b-a4cf-d658cd098522.png?v=1788280839","gingerbread":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/gingerbread_0dbf70bf-d658-42f6-ba4d-fd884d009343.png?v=1788280838","tomato-soup":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/tomato-soup_7bbe1c27-bab3-4a0b-aa8e-55afcde877fc.png?v=1788280838","you-woof-good":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/you-woof-good_a07a8488-2069-47cc-a7e0-86665248d723.png?v=1788280839","pride-heart":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/pride-heart_7bb9545f-6fa0-4cf7-8086-5bdd7d25e2c7.png?v=1788280839","love-heart":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/love-heart_cbfa7de5-8f14-4f09-bb24-60a2ed860779.png?v=1788280839","key":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/key_c6d0394f-2a26-426a-8b27-5d9829924d9e.png?v=1788280837","shell":"https://cdn.shopify.com/s/files/1/0773/8317/2315/files/shell_0934c6e1-2438-4dfa-8e5d-c9ce56d6dbcf.png?v=1788280836"};
  const slug = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const productHandle = (href) => (String(href || '').match(/\/products\/([^?#/]+)/) || [])[1] || '';
  const dogUrlForHandle = (handle) => DOG_CHARM_IMAGES[String(handle || '').replace(/-matching-charm$/, '')];

  const setDogImage = (img, url) => {
    if (!img || !url) return;
    if (!img.dataset.twiniOriginalSrc) img.dataset.twiniOriginalSrc = img.getAttribute('src') || '';
    if (!img.dataset.twiniOriginalSrcset) img.dataset.twiniOriginalSrcset = img.getAttribute('srcset') || '';
    img.setAttribute('src', url);
    img.setAttribute('srcset', '');
    img.style.objectFit = 'contain';
    img.style.backgroundColor = '#fff';
  };

  const restoreImage = (img) => {
    if (!img || !img.dataset.twiniOriginalSrc) return;
    img.setAttribute('src', img.dataset.twiniOriginalSrc);
    img.setAttribute('srcset', img.dataset.twiniOriginalSrcset || '');
  };

  const decorateDogCharms = () => {
    const view = new URLSearchParams(location.search).get('view');
    const dogCollection = view === 'dog-charms' || location.pathname === '/collections/dog-charms' || location.pathname === '/collections/dog-essentials';
    if (dogCollection) {
      document.querySelectorAll('a[href*="/products/"]').forEach((link) => {
        const url = dogUrlForHandle(productHandle(link.getAttribute('href')));
        if (!url) return;
        const card = link.closest('.card-wrapper,.grid__item');
        if (card) card.querySelectorAll('.card__media img').forEach((img) => setDogImage(img, url));
      });
    }

    if (location.pathname === '/collections/matching-sets') {
      document.querySelectorAll('.twp-charm-choice[data-design]').forEach((button) => {
        const url = DOG_CHARM_IMAGES[slug(button.dataset.design)];
        const img = button.querySelector('.twp-charm-thumb img');
        if (img && url) setDogImage(img, url);
      });
    }

    const handle = productHandle(location.pathname);
    const productUrl = dogUrlForHandle(handle);
    if (productUrl) {
      const radios = document.querySelectorAll('input[type="radio"]');
      const selected = Array.from(radios).find((radio) => radio.checked && /For Pup|For You|Matching Pair/i.test(radio.value || radio.nextElementSibling?.textContent || ''));
      const isForPup = !selected || /For Pup/i.test(selected.value || selected.nextElementSibling?.textContent || '');
      document.querySelectorAll('.product__media img,.product-media-container img').forEach((img) => isForPup ? setDogImage(img, productUrl) : restoreImage(img));
      radios.forEach((radio) => {
        if (radio.dataset.twiniDogImageBound) return;
        radio.dataset.twiniDogImageBound = 'true';
        radio.addEventListener('change', decorateDogCharms);
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', decorateDogCharms);
  else decorateDogCharms();
  new MutationObserver(decorateDogCharms).observe(document.documentElement, { childList: true, subtree: true });
})();


/* TwiniPaws Build Your Match v2 — Claude layout adapted for Shopify */
(() => {
  const initBuildMatchV2 = () => {
  if (location.pathname !== '/collections/matching-sets' || document.querySelector('#twp-match-v2')) return;

  const dogBase = 'https://cdn.shopify.com/s/files/1/0773/8317/2315/files/';
  const charms = [
    ['tent','Tent','Spring','A tiny adventure, packed for two.','tent_9f8505fa-5e37-4c33-b7eb-9b1848816c4e.png?v=1788280836'],
    ['cupcake','Cupcake','Spring','A little celebration for every ordinary day.','cupcake_98dfd435-76dc-4d96-89cf-5fe99cda2f75.png?v=1788280838'],
    ['pine-tree','Pine Tree','Spring','Fresh air, muddy paws, and nowhere else to be.','pine-tree_ae759512-2010-4f62-96b0-23f2503b4792.png?v=1788280839'],
    ['mushroom','Mushroom','Spring','For the small magic you find together.','mushroom_7addd4ec-6a2f-437e-a57f-cd57fabf5719.png?v=1788280836'],
    ['fox','Fox','Spring','Curious hearts always find the best paths.','fox_32036b1f-4188-4685-bb6c-24f4ff105a84.png?v=1788280836'],
    ['wave','Wave','Summer','One ocean, two happy hearts.','wave_b0baba37-4ff3-49b0-9c0d-e93f5e5e4d58.png?v=1788280838'],
    ['octopus','Octopus','Summer','Eight arms still would not be enough hugs.','octopus_a351adea-1c43-406d-a366-3ffe8a54698e.png?v=1788280838'],
    ['flower','Flower','Summer','You make every day bloom.','flower_4af0719e-9741-4c4a-9513-791b3ced9af0.png?v=1788280839'],
    ['butterfly','Butterfly','Summer','Growing together looks good on you.','butterfly_65a36cf3-5ffd-4ec6-a04e-5380c118205a.png?v=1788280839'],
    ['ice-cream','Ice Cream','Summer','Sweet days, sunny walks, happy paws.','ice-cream_c6237779-bfb6-4c82-9791-d94ef07555c2.png?v=1788280838'],
    ['hot-dog','Hot Dog','Autumn','The perfect pair for snack-sized adventures.','hot-dog_0c47b91d-20fe-45b0-8474-fa70352f708a.png?v=1788280837'],
    ['acorn','Acorn','Autumn','Little things grow into the biggest love.','acorn_b7ff1190-20b3-4f08-8eb5-f03fd86ee360.png?v=1788280838'],
    ['pumpkin','Pumpkin','Autumn','Cozy season is better together.','pumpkin_4c51af9b-feb0-4404-b841-cf43f7175113.png?v=1788280838'],
    ['deer','Deer','Autumn','Soft hearts belong in the wild.','deer_fd78567d-e4a1-4c3d-840b-30beb4b9ceda.png?v=1788280837'],
    ['owl','Owl','Autumn','Some cuddles are worth staying up for.','owl_86d9eff0-1ac9-4ffd-bea0-00a5f309c490.png?v=1788280838'],
    ['snowflake','Snowflake','Winter','No two bonds are ever the same.','snowflake_aaf159dd-38cf-49ab-906e-0436a2c6e83d.png?v=1788280838'],
    ['scarf','Scarf','Winter','Cold days are better wrapped up together.','scarf_6ce6759f-da5a-4d9b-8700-a014a2520eb0.png?v=1788280836'],
    ['snowman','Snowman','Winter','Can we stay out a little longer?','snowman_54695f68-817c-424b-a4cf-d658cd098522.png?v=1788280839'],
    ['gingerbread','Gingerbread','Winter','Something sweet for someone sweeter.','gingerbread_0dbf70bf-d658-42f6-ba4d-fd884d009343.png?v=1788280838'],
    ['tomato-soup','Tomato Soup','Winter','Something warm, someone close.','tomato-soup_7bbe1c27-bab3-4a0b-aa8e-55afcde877fc.png?v=1788280838'],
    ['you-woof-good','You Woof Good','Perfect Duo','Every day is better by your side.','you-woof-good_a07a8488-2069-47cc-a7e0-86665248d723.png?v=1788280839'],
    ['pride-heart','Pride Heart','Perfect Duo','Love looks good on everyone.','pride-heart_7bb9545f-6fa0-4cf7-8086-5bdd7d25e2c7.png?v=1788280839'],
    ['love-heart','Love Heart','Perfect Duo','I love you a billion million trillion.','love-heart_cbfa7de5-8f14-4f09-bb24-60a2ed860779.png?v=1788280839'],
    ['key','Key','Perfect Duo','You will always have the key to my heart.','key_c6d0394f-2a26-426a-8b27-5d9829924d9e.png?v=1788280837'],
    ['shell','Shell','Perfect Duo','What if mermaids had paws?','shell_0934c6e1-2438-4dfa-8e5d-c9ce56d6dbcf.png?v=1788280836']
  ].map(([slug,name,season,story,dog]) => ({slug,name,season,story,dog:dogBase+dog,human:`/cdn/shop/files/${slug}.png`}));

  const state = {mode:'Matching Pair', charm:charms[0], pup:'Collar', pupColor:'Sky Blue', pupSize:'S', human:'Bracelet', humanColor:'Ice Blue', humanSize:'S', qty:1, filter:'All'};
  const main = document.querySelector('main');
  if (!main) return;

  const style = document.createElement('style');
  style.textContent = `
  #twp-match-v2{--brown:#594239;--cream:#faf6ed;--blue:#aad4f3;--line:#ded7d1;color:var(--brown);background:var(--cream);padding:54px max(22px,5vw) 90px;font-family:inherit}
  #twp-match-v2 *{box-sizing:border-box}.tm-head{text-align:center;max-width:760px;margin:0 auto 38px}.tm-kicker{font-size:13px;letter-spacing:.18em;text-transform:uppercase;color:#789fbb}.tm-head h1{font-size:clamp(36px,5vw,64px);line-height:1.05;margin:12px 0 16px}.tm-head p{font-size:18px;line-height:1.65;color:#806d65}.tm-modes{display:flex;justify-content:center;gap:10px;flex-wrap:wrap;margin-top:24px}.tm-pill,.tm-filter,.tm-choice,.tm-size{border:1px solid var(--line);background:#fff;color:var(--brown);border-radius:999px;padding:11px 18px;font:inherit;cursor:pointer}.tm-pill.on,.tm-filter.on,.tm-choice.on,.tm-size.on{background:var(--blue);border-color:#78bcec}.tm-layout{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(330px,.65fr);gap:28px;align-items:start;max-width:1440px;margin:auto}.tm-left,.tm-panel{background:#fff;border:1px solid var(--line);border-radius:28px}.tm-left{padding:26px}.tm-preview{display:grid;grid-template-columns:1fr auto 1fr;gap:18px;align-items:center;padding:24px;background:#f7f2e9;border-radius:22px;margin-bottom:24px}.tm-slot{text-align:center;min-width:0}.tm-role{font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:#8e7d75}.tm-product{height:16px;border-radius:999px;margin:14px 12%;background:var(--blue)}.tm-charm-img{width:150px;height:150px;object-fit:contain;background:white;border-radius:24px;margin:2px auto 10px;display:block}.tm-slot-name{font-size:18px;font-weight:700}.tm-match-mark{text-align:center;color:#7badce;font-size:12px;text-transform:uppercase;letter-spacing:.12em}.tm-match-mark b{display:block;font-size:26px}.tm-grid-head{display:flex;justify-content:space-between;gap:18px;align-items:end;margin:4px 0 14px}.tm-grid-head h2{font-size:24px;margin:0}.tm-count{font-size:13px;color:#95847d}.tm-filters{display:flex;gap:8px;overflow:auto;padding:0 0 16px}.tm-filter{white-space:nowrap;padding:8px 13px;font-size:13px}.tm-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px}.tm-card{border:1px solid var(--line);border-radius:18px;background:#fff;padding:11px;cursor:pointer;text-align:left;color:var(--brown);transition:.18s}.tm-card:hover{transform:translateY(-2px);border-color:#9bcaeb}.tm-card.on{background:#eaf6fe;border:2px solid #86c5ef;padding:10px}.tm-pair-img{display:grid;grid-template-columns:1fr 1fr;gap:5px}.tm-pair-img img{width:100%;aspect-ratio:1;object-fit:contain;background:#fff;border-radius:12px}.tm-card strong{display:block;font-size:13px;margin-top:8px}.tm-card small{font-size:11px;color:#97857d}.tm-panel{position:sticky;top:24px;padding:26px}.tm-selected{display:grid;grid-template-columns:76px 1fr;gap:14px;align-items:center;padding-bottom:22px;border-bottom:1px solid var(--line)}.tm-selected img{width:76px;height:76px;object-fit:contain;border-radius:15px;background:#f7f2e9}.tm-selected h2{font-size:23px;margin:0 0 6px}.tm-selected p{margin:0;font-size:13px;line-height:1.45;color:#8c7971}.tm-section{padding:20px 0;border-bottom:1px solid var(--line)}.tm-label{font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;margin-bottom:11px}.tm-step{display:inline-grid;place-items:center;width:24px;height:24px;border-radius:50%;background:var(--blue);margin-right:8px}.tm-choices,.tm-sizes{display:flex;gap:8px;flex-wrap:wrap}.tm-choice,.tm-size{padding:9px 13px;font-size:13px}.tm-swatches{display:flex;gap:10px;margin-top:13px}.tm-swatch{width:30px;height:30px;border:3px solid white;box-shadow:0 0 0 1px #cfc6c1;border-radius:50%;cursor:pointer}.tm-swatch.on{box-shadow:0 0 0 3px #7dc0eb}.tm-summary{padding-top:20px}.tm-summary-line{display:flex;justify-content:space-between;font-size:14px;margin:8px 0}.tm-summary-line.total{font-size:20px;font-weight:700;margin-top:15px}.tm-qty{display:flex;align-items:center;justify-content:space-between;margin:20px 0}.tm-qty-control{display:flex;align-items:center;border:1px solid var(--line);border-radius:999px;overflow:hidden}.tm-qty-control button{border:0;background:#fff;width:42px;height:40px;font-size:20px;cursor:pointer}.tm-qty-control span{width:34px;text-align:center}.tm-atc{width:100%;border:0;border-radius:999px;background:var(--brown);color:white;padding:16px;font:inherit;font-weight:700;cursor:pointer}.tm-atc:disabled{opacity:.55}.tm-note{text-align:center;font-size:11px;color:#95847d;margin:10px 0 0}.tm-toast{position:fixed;right:22px;bottom:22px;background:var(--brown);color:#fff;padding:14px 20px;border-radius:999px;z-index:9999;opacity:0;transform:translateY(12px);pointer-events:none;transition:.2s}.tm-toast.show{opacity:1;transform:none}
  @media(max-width:1050px){.tm-grid{grid-template-columns:repeat(4,1fr)}.tm-layout{grid-template-columns:1fr}.tm-panel{position:static}}
  @media(max-width:700px){#twp-match-v2{padding:34px 14px 65px}.tm-left,.tm-panel{border-radius:20px;padding:16px}.tm-preview{grid-template-columns:1fr 1fr;padding:16px;gap:10px}.tm-match-mark{display:none}.tm-charm-img{width:112px;height:112px}.tm-grid{grid-template-columns:repeat(2,1fr)}.tm-card strong{font-size:12px}.tm-head p{font-size:16px}.tm-grid-head{align-items:start}.tm-count{display:none}}
  `;
  document.head.appendChild(style);

  main.innerHTML = `<section id="twp-match-v2"><header class="tm-head"><div class="tm-kicker">Build Your Match</div><h1>Choose your charm.<br>Build your set.</h1><p>Pick a piece for your pup, one for you, and a charm that tells the same little story.</p><div class="tm-modes">${['Matching Pair','For Pup','For You'].map(x=>`<button class="tm-pill${x===state.mode?' on':''}" data-mode="${x}">${x}</button>`).join('')}</div></header><div class="tm-layout"><div class="tm-left"><div class="tm-preview"><div class="tm-slot tm-pup"><div class="tm-role">Your pup wears</div><div class="tm-product"></div><img class="tm-charm-img" alt="Dog charm preview"><div class="tm-slot-name"></div></div><div class="tm-match-mark"><b>×</b>matched</div><div class="tm-slot tm-you"><div class="tm-role">You wear</div><div class="tm-product"></div><img class="tm-charm-img" alt="Human charm preview"><div class="tm-slot-name"></div></div></div><div class="tm-grid-head"><h2>Select a charm</h2><span class="tm-count"></span></div><div class="tm-filters">${['All','Spring','Summer','Autumn','Winter','Perfect Duo'].map(x=>`<button class="tm-filter${x==='All'?' on':''}" data-filter="${x}">${x}</button>`).join('')}</div><div class="tm-grid"></div></div><aside class="tm-panel"><div class="tm-selected"><img alt="Selected charm"><div><h2></h2><p></p></div></div><div class="tm-section tm-pup-controls"><div class="tm-label"><span class="tm-step">1</span>For your pup</div><div class="tm-choices">${['Collar','Harness','Leash'].map(x=>`<button class="tm-choice${x===state.pup?' on':''}" data-pup="${x}">${x}</button>`).join('')}</div><div class="tm-swatches">${[['Sky Blue','#aad4f3'],['Walnut','#8b6040'],['Sage','#7ec890'],['Blossom','#f0a090']].map(([n,c])=>`<button class="tm-swatch${n===state.pupColor?' on':''}" data-pup-color="${n}" title="${n}" style="background:${c}"></button>`).join('')}</div><div class="tm-label" style="margin-top:15px">Size</div><div class="tm-sizes">${['XS','S','M','L','XL'].map(x=>`<button class="tm-size${x===state.pupSize?' on':''}" data-pup-size="${x}">${x}</button>`).join('')}</div></div><div class="tm-section tm-human-controls"><div class="tm-label"><span class="tm-step">2</span>For you</div><div class="tm-choices">${['Bracelet','Necklace'].map(x=>`<button class="tm-choice${x===state.human?' on':''}" data-human="${x}">${x}</button>`).join('')}</div><div class="tm-swatches">${[['Ice Blue','#aad4f3'],['Caramel','#c49a70'],['Cream','#e0d0b8']].map(([n,c])=>`<button class="tm-swatch${n===state.humanColor?' on':''}" data-human-color="${n}" title="${n}" style="background:${c}"></button>`).join('')}</div><div class="tm-label" style="margin-top:15px">Size</div><div class="tm-sizes">${['S','M','L'].map(x=>`<button class="tm-size${x===state.humanSize?' on':''}" data-human-size="${x}">${x}</button>`).join('')}</div></div><div class="tm-summary"><div class="tm-label"><span class="tm-step">3</span>Your match</div><div class="tm-summary-line"><span>Selected charm</span><strong>$19.99</strong></div><div class="tm-summary-line"><span>Made for</span><strong class="tm-made-for"></strong></div><div class="tm-summary-line total"><span>Total</span><span class="tm-total"></span></div><div class="tm-qty"><span>Quantity</span><div class="tm-qty-control"><button data-qty="-1">−</button><span>1</span><button data-qty="1">+</button></div></div><button class="tm-atc">Add Charm to Cart</button><p class="tm-note">Base pieces are preview selections and will be connected as products when available.</p></div></aside></div><div class="tm-toast">Added to cart ✓</div></section>`;

  const $ = (s) => main.querySelector(s), $$ = (s) => [...main.querySelectorAll(s)];
  const humanSrc = c => `/cdn/shop/files/human-hook-v2-${c.slug}.png`;
  const visible = () => charms.filter(c => state.filter === 'All' || c.season === state.filter);
  function renderGrid(){
    $('.tm-grid').innerHTML = visible().map(c=>`<button class="tm-card${c.slug===state.charm.slug?' on':''}" data-charm="${c.slug}"><span class="tm-pair-img"><img src="${c.dog}" alt="${c.name} dog charm"><img src="${humanSrc(c)}" alt="${c.name} human charm"></span><strong>${c.name}</strong><small>${c.season}</small></button>`).join('');
    $('.tm-count').textContent = `${visible().length} designs`;
  }
  function render(){
    const c=state.charm;
    $('.tm-pup .tm-charm-img').src=c.dog; $('.tm-pup .tm-slot-name').textContent=c.name+' · Pup';
    $('.tm-you .tm-charm-img').src=humanSrc(c); $('.tm-you .tm-slot-name').textContent=c.name+' · You';
    $('.tm-pup').style.display=state.mode==='For You'?'none':''; $('.tm-you').style.display=state.mode==='For Pup'?'none':''; $('.tm-match-mark').style.display=state.mode==='Matching Pair'?'':'none';
    $('.tm-preview').style.gridTemplateColumns=state.mode==='Matching Pair'?'1fr auto 1fr':'1fr';
    $('.tm-pup .tm-product').style.background=({SkyBlue:'#aad4f3',Walnut:'#8b6040',Sage:'#7ec890',Blossom:'#f0a090'})[state.pupColor.replace(' ','')]||'#aad4f3';
    $('.tm-you .tm-product').style.background=({IceBlue:'#aad4f3',Caramel:'#c49a70',Cream:'#e0d0b8'})[state.humanColor.replace(' ','')]||'#aad4f3';
    $('.tm-selected img').src=state.mode==='For You'?humanSrc(c):c.dog; $('.tm-selected h2').textContent=c.name+' Charm'; $('.tm-selected p').textContent=c.story;
    $('.tm-made-for').textContent=state.mode; $('.tm-total').textContent='$'+(19.99*state.qty).toFixed(2); $('.tm-qty-control span').textContent=state.qty;
    $('.tm-pup-controls').style.display=state.mode==='For You'?'none':''; $('.tm-human-controls').style.display=state.mode==='For Pup'?'none':'';
    $$('.tm-pill').forEach(b=>b.classList.toggle('on',b.dataset.mode===state.mode));
    $$('.tm-card').forEach(b=>b.classList.toggle('on',b.dataset.charm===c.slug));
  }
  main.addEventListener('click', async e => {
    const b=e.target.closest('button'); if(!b)return;
    if(b.dataset.charm||b.classList.contains('tm-atc'))return;
    if(b.dataset.mode){state.mode=b.dataset.mode;render();}
    if(b.dataset.filter){state.filter=b.dataset.filter;$$('.tm-filter').forEach(x=>x.classList.toggle('on',x===b));renderGrid();render();}
    if(b.dataset.charm){state.charm=charms.find(c=>c.slug===b.dataset.charm);render();}
    if(b.dataset.pup){state.pup=b.dataset.pup;$$('[data-pup]').forEach(x=>x.classList.toggle('on',x===b));}
    if(b.dataset.human){state.human=b.dataset.human;$$('[data-human]').forEach(x=>x.classList.toggle('on',x===b));}
    if(b.dataset.pupColor){state.pupColor=b.dataset.pupColor;$$('[data-pup-color]').forEach(x=>x.classList.toggle('on',x===b));render();}
    if(b.dataset.humanColor){state.humanColor=b.dataset.humanColor;$$('[data-human-color]').forEach(x=>x.classList.toggle('on',x===b));render();}
    if(b.dataset.pupSize){state.pupSize=b.dataset.pupSize;$$('[data-pup-size]').forEach(x=>x.classList.toggle('on',x===b));}
    if(b.dataset.humanSize){state.humanSize=b.dataset.humanSize;$$('[data-human-size]').forEach(x=>x.classList.toggle('on',x===b));}
    if(b.dataset.qty){state.qty=Math.max(1,state.qty+Number(b.dataset.qty));render();}
    if(b.classList.contains('tm-atc')){
      b.disabled=true;b.textContent='Adding…';
      try{
        const p=await fetch(`/products/${state.charm.slug}-matching-charm.js`).then(r=>r.json());
        const wanted=state.mode==='Matching Pair'?'Matching Pair':state.mode;
        const v=p.variants.find(v=>v.title.trim().toLowerCase()===wanted.toLowerCase())||p.variants.find(v=>v.available)||p.variants[0];
        await fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:v.id,quantity:state.qty,properties:{'Pup piece':state.mode==='For You'?'—':`${state.pup} · ${state.pupColor} · ${state.pupSize}`,'Your piece':state.mode==='For Pup'?'—':`${state.human} · ${state.humanColor} · ${state.humanSize}`}}]})}).then(r=>{if(!r.ok)throw Error('cart');return r.json()});
        const t=$('.tm-toast');t.classList.add('show');setTimeout(()=>t.classList.remove('show'),1800);document.dispatchEvent(new CustomEvent('cart:refresh'));
      }catch(err){alert('This charm could not be added. Please open its product page and try again.');}
      b.disabled=false;b.textContent='Add Charm to Cart';
    }
  });
    /* TwiniPaws multi-charm preview v3 — scoped enhancement, 2026-09-04 */
  const selectedCharms = new Set([charms[0].slug]);
  const productCache = new Map();
  let multiBusy = false, priceRevision = 0;
  state.human = 'Necklace';
  const baseCatalog = {Collar:'dog-collar',Harness:'dog-harness',Leash:'dog-leash',Bracelet:'twinipaws-purple-collection',Necklace:'human-necklace'};
  const baseProducts = new Map();
  let baseBusy=false;
  const palette = {Blue:'#9bc9e7',Purple:'#b9a5d5',Red:'#df8c89',Black:'#55545a',Green:'#91c5ab',Yellow:'#ebcf7b'};
  state.pupColor = 'Blue';
  const multiStyle = document.createElement('style');
  multiStyle.textContent = `
  #twp-match-v2 .tm-preview{display:grid!important;grid-template-columns:minmax(0,1fr) minmax(0,1fr)!important;align-items:start;gap:16px;padding:16px;background:#faf8f4}
  #twp-match-v2 .tm-preview .tm-slot{width:100%;min-width:0}
  #twp-match-v2 .tm-preview.tm-single{grid-template-columns:minmax(0,1fr)!important}
  #twp-match-v2 .tm-preview.tm-single .tm-scene{aspect-ratio:5.4}
  #twp-match-v2 .tm-preview .tm-role{text-align:left;font-size:13px}
  #twp-match-v2 .tm-product,#twp-match-v2 .tm-charm-img{display:none!important}
  #twp-match-v2 .tm-scene-track{display:flex;overflow-x:auto;scroll-snap-type:x mandatory;gap:12px;margin:14px 0;scrollbar-width:thin}
  #twp-match-v2 .tm-scene{position:relative;flex:0 0 100%;aspect-ratio:2.7;min-height:95px;background:#fff;border-radius:14px;overflow:hidden;scroll-snap-align:start;isolation:isolate}
  #twp-match-v2 .tm-base-guide{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:0;opacity:.65}
  #twp-match-v2 .tm-scene img{position:absolute;display:block!important;object-fit:contain;background:transparent!important;mix-blend-mode:multiply;border:0!important;border-radius:0!important;transform:translate(-50%,-50%);margin:0!important;padding:0!important;pointer-events:none}
  #twp-match-v2 .tm-scene-caption{font-size:13px;line-height:1.5;color:#806d65;margin:5px 0}
  #twp-match-v2 .tm-match-mark{display:none!important}
  #twp-match-v2 .tm-preview .tm-slot-name{font-size:14px;font-weight:400;color:#8e7d75}
  #twp-match-v2 .tm-card{position:relative}
  #twp-match-v2 .tm-card.on:after{content:'✓';position:absolute;top:5px;right:5px;width:24px;height:24px;display:grid;place-items:center;border-radius:50%;background:#aad4f3;color:#263b4c}
  #twp-match-v2 .tm-selected{grid-template-columns:1fr}
  #twp-match-v2 .tm-selected>img{display:none}
  #twp-match-v2 .tm-picked-list{display:flex;flex-wrap:wrap;gap:7px;padding:16px 0}
  #twp-match-v2 .tm-remove{border:1px solid #9bc9e7;border-radius:999px;padding:7px 12px;background:#edf7ff;color:#594239;font:inherit;font-size:14px;cursor:pointer}
  #twp-match-v2 .tm-multi-status{font-size:14px;line-height:1.5;color:#594239;padding:10px 0}
  #twp-match-v2 .tm-note{font-size:13px;line-height:1.6}
  #twp-match-v2 .tm-panel{background:#eee2d6;border-color:#d5c1af}
  #twp-match-v2 .tm-panel .tm-selected p,#twp-match-v2 .tm-panel .tm-note{color:#6a5549}
  #twp-match-v2 .tm-base-quantity{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:16px;font-size:14px}
  #twp-match-v2 .tm-base-add{padding:10px 24px;border:1px solid #594239;border-radius:999px;background:#594239;color:#fff;font:inherit;cursor:pointer}
  #twp-match-v2 .tm-base-add:disabled{opacity:.45;cursor:not-allowed}
  #twp-match-v2 .tm-cart-items{display:grid;gap:8px;padding:12px 0;font-size:13px;line-height:1.5}
  #twp-match-v2 .tm-cart-row{display:flex;gap:10px;align-items:center;background:#fff9;padding:8px;border-radius:12px}
  #twp-match-v2 .tm-cart-row img{width:42px;height:42px;object-fit:contain;border-radius:8px}
  #twp-match-v2 .tm-base-message{font-size:12px;line-height:1.5;color:#6a5549;margin:9px 0 0}
  #twp-match-v2 .tm-swatch:disabled{opacity:.3;cursor:not-allowed;position:relative}
  #twp-match-v2 .tm-swatch:disabled:after{content:'';position:absolute;width:28px;height:2px;background:#594239;left:-2px;top:11px;transform:rotate(-45deg)}
  #twp-match-v2 .tm-guidance{font-size:14px;line-height:1.7;color:#745c4e;background:#faf6ed;border-radius:14px;padding:16px;margin:0 0 20px}
  #twp-match-v2 .tm-scene-track:focus-visible,#twp-match-v2 button:focus-visible{outline:3px solid #4593ca;outline-offset:3px}
  @media(max-width:600px){#twp-match-v2 .tm-preview{padding:8px;gap:8px}#twp-match-v2 .tm-scene-track{width:100%;margin:8px auto}#twp-match-v2 .tm-scene{min-height:85px;aspect-ratio:1.8}#twp-match-v2 .tm-preview .tm-role{font-size:11px;letter-spacing:.06em}#twp-match-v2 .tm-scene-caption,#twp-match-v2 .tm-preview .tm-slot-name{font-size:11px;line-height:1.4}}
  `;
  document.head.appendChild(multiStyle);
  const sept8Style=document.createElement('style');
  sept8Style.textContent=`
  #twp-match-v2 .tm-scene img{mix-blend-mode:normal;filter:url(#tm-white-key);z-index:2!important}
  #twp-match-v2 .tm-preview .tm-scene-caption,#twp-match-v2 .tm-preview .tm-slot-name{display:none}
  #twp-match-v2 .tm-step-description{font-size:13px;line-height:1.6;margin:0 0 16px;color:#6a5549}
  #twp-match-v2 .tm-charm-step{padding:20px 0;border-bottom:1px solid var(--line)}
  #twp-match-v2 .tm-charm-step .tm-selected{border:0;padding:0}
  #twp-match-v2 .tm-charm-step .tm-selected h2{font-size:16px}
  #twp-match-v2 .tm-charm-step .tm-selected p{display:none}
  #twp-match-v2 .tm-panel>.tm-section:first-child{padding-top:0}
  #twp-match-v2 .tm-cart-section{margin-top:20px;padding-top:20px;border-top:1px solid var(--line)}
  #twp-match-v2 .tm-cart-items{max-height:220px;overflow:auto}
  #twp-match-v2 .tm-preview .tm-role{text-transform:none;letter-spacing:.08em}
  `;
  document.head.appendChild(sept8Style);
  main.insertAdjacentHTML('beforeend','<svg width="0" height="0" aria-hidden="true" style="position:absolute;pointer-events:none"><defs><filter id="tm-white-key" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 -100 -100 -100 0 299"/><feComposite in2="SourceGraphic" operator="in"/></filter></defs></svg>');
  $('.tm-head p').textContent = 'Choose your charms and see them together. Tap a selected charm again to remove it.';
  $('.tm-grid-head h2').textContent = 'Choose your charms';
  $('.tm-note').textContent = 'Use Add to put each base piece directly in your cart. The button below adds your selected charms only.';
  $('.tm-grid-head').insertAdjacentHTML('beforebegin','<p class="tm-guidance">Mix and match freely here, we recommend up to 5 charms on collars, around 5–7 on leashes, and 1–2 on harnesses depending on size. You can always collect a few extras to switch things up anytime.</p>');
  $('.tm-qty > span').textContent = 'Matching Sets';
  const humanControls = $('.tm-human-controls');
  humanControls.querySelector('.tm-swatches').remove();
  humanControls.querySelector('.tm-sizes').remove();
  humanControls.querySelectorAll('.tm-label')[1].remove();
  for(const side of ['pup','human']) $('.tm-'+side+'-controls').insertAdjacentHTML('beforeend',`<div class="tm-base-quantity"><span class="tm-base-quantity-label"></span><button type="button" class="tm-base-add" data-add-base="${side}">Add</button></div><p class="tm-base-message" role="status"></p>`);
  $('.tm-selected').insertAdjacentHTML('afterend','<section aria-label="Items in your cart"><h3 style="font-size:16px;margin-bottom:0">Added to your cart</h3><div class="tm-cart-items" aria-live="polite">Loading your cart…</div><a href="/cart" style="font-size:13px">View cart</a></section>');
  async function syncCart(){
    try{const r=await fetch('/cart.js');if(!r.ok)throw Error();const cart=await r.json(),box=$('.tm-cart-items');box.replaceChildren();
      for(const item of cart.items){const row=document.createElement('div');row.className='tm-cart-row';if(item.image){const img=document.createElement('img');img.src=item.image;img.alt='';row.appendChild(img);}const label=document.createElement('span');label.textContent=`${item.product_title||item.title}${item.variant_title&&item.variant_title!=='Default Title'?' · '+item.variant_title:''} × ${item.quantity}`;row.appendChild(label);box.appendChild(row);}
      if(!cart.items.length)box.textContent='Nothing added yet.';
      const count=document.querySelector('#cart-icon-bubble .cart-count-bubble');if(count){const visual=count.querySelector('[aria-hidden="true"]'),sr=count.querySelector('.visually-hidden');if(visual)visual.textContent=cart.item_count;if(sr)sr.textContent=`${cart.item_count} items`;}
    }catch{$('.tm-cart-items').textContent='Unable to refresh cart. Open View cart to check your items.';}
  }
  syncCart();window.addEventListener('pageshow',syncCart);
  function currentBaseVariant(type) {
    const p=baseProducts.get(type); if(!p)return null;
    if(type==='Bracelet'||type==='Necklace'){
      if(!Array.isArray(p.variants)||p.variants.length!==1)return null;
      const variant=p.variants[0];
      return String(variant.title).trim().toLowerCase()==='default title'?variant:null;
    }
    if(state.pupSize==='L'&&state.pupColor==='Black'&&type!=='Leash')return null;
    return p.variants.find(v=>{
      const opts=v.options.map(String).map(s=>s.trim().toLowerCase());
      return opts.includes(state.pupColor.toLowerCase())&&(type==='Leash'||opts.includes(state.pupSize.toLowerCase()));
    });
  }
  function renderBaseControls(){
    const sizes=state.pup==='Collar'?['S','M','L']:state.pup==='Harness'?['XS','S','M','L']:[];
    if(sizes.length&&!sizes.includes(state.pupSize))state.pupSize='S';
    if(state.pupSize==='L'&&state.pupColor==='Black'&&state.pup!=='Leash')state.pupColor='Blue';
    $('.tm-pup-controls .tm-swatches').innerHTML=Object.entries(palette).map(([name,color])=>`<button class="tm-swatch${state.pupColor===name?' on':''}" data-pup-color="${name}" title="${name}" aria-label="${name}" aria-pressed="${state.pupColor===name}" style="background:${color}" ${state.pupSize==='L'&&name==='Black'&&state.pup!=='Leash'?'disabled':''}></button>`).join('');
    $('.tm-pup-controls .tm-sizes').innerHTML=sizes.map(s=>`<button class="tm-size${s===state.pupSize?' on':''}" data-pup-size="${s}">${s}</button>`).join('');
    $('.tm-pup-controls').querySelectorAll('.tm-label')[1].style.display=sizes.length?'':'none';
    for(const side of ['pup','human']){
      const type=state[side],root=$('.tm-'+side+'-controls'),button=root.querySelector('.tm-base-add');
      button.disabled=baseBusy||multiBusy||!currentBaseVariant(type)?.available;
      button.textContent=baseBusy?'Adding…':'Add';
      root.querySelector('.tm-base-quantity-label').textContent=type;
      root.querySelector('.tm-base-message').textContent=baseProducts.has(type)?(currentBaseVariant(type)?.available?'Add one to your cart. Tap again to add another.':side==='human'?'This item is unavailable. Please open its product page.':'This size/color is unavailable. Choose another option.'):'Checking base availability…';
    }
  }
  $('.tm-selected').insertAdjacentHTML('afterend','<div class="tm-picked-list" aria-label="Selected charms"></div>');
  const panel=$('.tm-panel'),pupControls=$('.tm-pup-controls');
  const stepHeading=(n,title)=>`<span class="tm-step" aria-label="Step ${n}">${n}</span>${title}`;
  pupControls.querySelector('.tm-label').innerHTML=stepHeading(1,'SELECT YOUR PUP’S BASE');
  pupControls.querySelector('.tm-label').insertAdjacentHTML('afterend','<p class="tm-step-description">Choose a collar, harness, or leash.<br>Select a size and color.</p>');
  humanControls.querySelector('.tm-label').innerHTML=stepHeading(2,'SELECT YOUR BASE');
  humanControls.querySelector('.tm-label').insertAdjacentHTML('afterend','<p class="tm-step-description">Choose a bracelet or necklace.</p>');
  const charmStep=document.createElement('section');charmStep.className='tm-charm-step';
  charmStep.innerHTML=`<div class="tm-label">${stepHeading(3,'SELECT YOUR CHARMS')}</div><p class="tm-step-description">Choose one or more designs for you and your pup.</p>`;
  charmStep.append($('.tm-selected'),$('.tm-picked-list'));
  const summary=$('.tm-summary');
  summary.querySelector('.tm-label').innerHTML=stepHeading(4,'FINALIZE YOUR MATCH');
  summary.querySelector('.tm-label').insertAdjacentHTML('afterend','<p class="tm-step-description">Review your selections and adjust the quantity if needed.</p>');
  const cartSection=panel.querySelector('section[aria-label="Items in your cart"]');cartSection.classList.add('tm-cart-section');
  panel.append(pupControls,humanControls,charmStep,summary,cartSection);
  summary.insertBefore($('.tm-qty'),$('.tm-summary-line.total'));
  $('.tm-pup .tm-role').textContent='For your pup';
  $('.tm-you .tm-role').textContent='For you';
  $('.tm-atc').insertAdjacentHTML('beforebegin','<div class="tm-multi-status" role="status" aria-live="polite"></div>');
  for (const slot of $$('.tm-slot')) slot.querySelector('.tm-role').insertAdjacentHTML('afterend','<div class="tm-scene-track" tabindex="0" aria-label="Selected charms on base; scroll for more"></div><p class="tm-scene-caption"></p>');
  const chosen = () => charms.filter(c => selectedCharms.has(c.slug));
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:window.Shopify?.currency?.active || 'USD'}).format(cents / 100);
  async function loadProduct(c) {
    if (!productCache.has(c.slug)) {
      productCache.set(c.slug, fetch(`/products/${c.slug}-matching-charm.js`).then(async r => {
        if (!r.ok) throw Error(`Unable to load ${c.name}. Please try again.`);
        return r.json();
      }).catch(err => { productCache.delete(c.slug); throw err; }));
    }
    return productCache.get(c.slug);
  }
  async function resolveItems(list, mode, quantity) {
    const items=await Promise.all(list.map(async c => {
      const p = await loadProduct(c);
      const v = p.variants.find(v => v.title.trim().toLowerCase() === mode.toLowerCase());
      if (!v || !v.available) throw Error(`${c.name} (${mode}) is unavailable. Remove it or choose another option.`);
      return {id:v.id,quantity,price:Number(v.price)};
    }));
    return items;
  }
  async function refreshPrice() {
    const revision = ++priceRevision, list = chosen();
    const button = $('.tm-atc');
    button.disabled = true;
    $('.tm-total').textContent = list.length ? 'Checking…' : money(0);
    $('.tm-multi-status').textContent = list.length ? '' : 'Select at least one charm to begin.';
    if (!list.length) return;
    try {
      const items = await resolveItems(list,state.mode,state.qty);
      if (revision !== priceRevision) return;
      $('.tm-total').textContent = money(items.reduce((sum,item) => sum + item.price * item.quantity,0));
      button.disabled = multiBusy;
    } catch (err) {
      if (revision !== priceRevision) return;
      $('.tm-total').textContent = '—';
      $('.tm-multi-status').textContent = err.message;
    }
  }
  function renderScenes(list) {
    const groups = [];
    const perGroup = window.matchMedia?.('(max-width:600px)')?.matches ? 3 : 5;
    for(let i=0;i<list.length;i+=perGroup) groups.push(list.slice(i,i+perGroup));
    if (!groups.length) groups.push([]);
    for(const side of ['pup','you']) {
      const slot = $(`.tm-${side}`);
      let guide = side==='pup'
        ? '<svg class="tm-base-guide" viewBox="0 0 1000 280" preserveAspectRatio="none" aria-hidden="true"><rect x="48" y="122" width="904" height="36" rx="18" fill="#e2eef6" stroke="#c3d8e5" stroke-width="3"/><rect x="48" y="117" width="48" height="46" rx="12" fill="#f3f8fb" stroke="#bfd3e0" stroke-width="4"/><path d="M62 140h24" stroke="#bfd3e0" stroke-width="3" stroke-linecap="round"/><path d="M906 140h24" stroke="#bfd3e0" stroke-width="5" stroke-dasharray="1 10" stroke-linecap="round"/></svg>'
        : '<svg class="tm-base-guide" viewBox="0 0 1000 280" preserveAspectRatio="none" aria-hidden="true"><path d="M55 89H945" fill="none" stroke="#e2d8c7" stroke-width="10" stroke-linecap="round"/><path d="M55 89H945" fill="none" stroke="#faf7f0" stroke-width="4" stroke-dasharray="12 9" stroke-linecap="round"/><ellipse cx="48" cy="89" rx="12" ry="9" fill="none" stroke="#d5c9b4" stroke-width="3"/><ellipse cx="952" cy="89" rx="12" ry="9" fill="none" stroke="#d5c9b4" stroke-width="3"/></svg>';
      slot.querySelector('.tm-scene-track').innerHTML = groups.map(group => `<div class="tm-scene" role="img" aria-label="${side==='pup'?'Flat collar':'Straight chain'} with ${group.length ? group.map(c=>c.name).join(', ') : 'no charms'}">${guide}${group.map((c,i) => {
        const x = 50 + (i-(group.length-1)/2) * Math.min(26,68/Math.max(1,group.length-1));
        const y = side==='pup' ? 50 : (state.mode==='Matching Pair' && group.length>3 ? 49 : 59);
        const size = group.length>3 ? 16 : 24;
        return `<img src="${side==='pup'?c.dog:humanSrc(c)}" alt="${c.name}" style="left:${x}%;top:${y}%;width:${size}%;height:76%;z-index:1">`;
      }).join('')}</div>`).join('');
      if(side==='pup')slot.querySelectorAll('.tm-base-guide rect').forEach(r=>r.setAttribute('fill',palette[state.pupColor]));
      slot.querySelector('.tm-slot-name').textContent = side==='pup' ? `Your charms · ${state.pupColor} ${state.pup.toLowerCase()}` : `Your charms · ${state.human.toLowerCase()}`;
      slot.querySelector('.tm-scene-caption').textContent = groups.length>1 ? `${list.length} charms · scroll to see all ${groups.length} preview groups` : `${list.length} charm${list.length===1?'':'s'} selected · styling preview`;
    }
  }
  window.matchMedia?.('(max-width:600px)')?.addEventListener?.('change',()=>renderScenes(chosen()));
  const originalRender = render;
  render = function() {
    originalRender();
    renderBaseControls();
    $('.tm-preview').classList.toggle('tm-single',state.mode!=='Matching Pair');
    const list = chosen();
    $$('.tm-card').forEach(b => { const on=selectedCharms.has(b.dataset.charm);b.classList.toggle('on',on);b.setAttribute('aria-pressed',String(on)); });
    $('.tm-selected h2').textContent = `${list.length} charm${list.length===1?'':'s'} selected`;
    $('.tm-selected p').textContent = list.length ? 'Your little collection, made to match.' : 'Choose a design below to start your match.';
    $('.tm-picked-list').innerHTML = list.map(c=>`<button class="tm-remove" data-remove-charm="${c.slug}" aria-label="Remove ${c.name}">${c.name} ×</button>`).join('');
    const firstLine=$('.tm-summary-line'); firstLine.querySelector('span').textContent='Selected designs';firstLine.querySelector('strong').textContent=String(list.length);
    $('.tm-atc').textContent = multiBusy ? 'Adding…' : 'ADD YOUR MATCH TO CART';
    $$('.tm-choice[data-human]').forEach(b=>b.classList.toggle('on',b.dataset.human===state.human));
    renderScenes(list);
    refreshPrice();
  };
  main.addEventListener('click',async e=>{
    const b=e.target.closest('button');if(!b||!b.closest('#twp-match-v2'))return;
    if(multiBusy||baseBusy){e.preventDefault();e.stopImmediatePropagation();return;}
    if(b.disabled)return;
    if(b.dataset.addBase){
      e.preventDefault();e.stopImmediatePropagation();const side=b.dataset.addBase,type=state[side],variant=currentBaseVariant(type),message=$('.tm-'+side+'-controls .tm-base-message');if(!variant?.available)return;
      baseBusy=true;renderBaseControls();
      try{const r=await fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:variant.id,quantity:1}]})});const data=await r.json();if(!r.ok)throw Error(data.description||'Could not add this item.');await syncCart();document.dispatchEvent(new CustomEvent('cart:refresh'));baseBusy=false;renderBaseControls();message.textContent=`${type} added to your cart.`;}
      catch(err){baseBusy=false;renderBaseControls();message.textContent=err.message;}
      return;
    }
    if(b.dataset.pup||b.dataset.human||b.dataset.pupColor||b.dataset.pupSize){
      e.preventDefault();e.stopImmediatePropagation();
      if(b.dataset.pup)state.pup=b.dataset.pup;
      if(b.dataset.human)state.human=b.dataset.human;
      if(b.dataset.pupColor)state.pupColor=b.dataset.pupColor;
      if(b.dataset.pupSize)state.pupSize=b.dataset.pupSize;
      $$('[data-pup]').forEach(x=>x.classList.toggle('on',x.dataset.pup===state.pup));
      render();return;
    }
    const slug=b.dataset.charm||b.dataset.removeCharm;
    if(slug){e.preventDefault();e.stopImmediatePropagation();if(selectedCharms.has(slug))selectedCharms.delete(slug);else selectedCharms.add(slug);render();return;}
    if(!b.classList.contains('tm-atc'))return;
    e.preventDefault();e.stopImmediatePropagation();
    const list=chosen(),mode=state.mode,quantity=state.qty;if(!list.length)return;
    multiBusy=true;++priceRevision;b.disabled=true;b.textContent='Adding…';
    try{
      const items=await resolveItems(list,mode,quantity);
      const result=await fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:items.map(({id,quantity})=>({id,quantity}))})});
      const data=await result.json();if(!result.ok)throw Error(data.description||'Unable to add these charms. Please try again.');
      $('.tm-multi-status').innerHTML=`Your selected items have been added. <a href="/cart">View cart</a>`;
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      await syncCart();
      fetch('/cart.js').then(r=>r.json()).then(cart=>{const count=document.querySelector('#cart-icon-bubble .cart-count-bubble');if(count){const visual=count.querySelector('[aria-hidden="true"]'),sr=count.querySelector('.visually-hidden');if(visual)visual.textContent=cart.item_count;if(sr)sr.textContent=`${cart.item_count} items`;}}).catch(()=>{});
    }catch(err){$('.tm-multi-status').textContent=err.message;}
    finally{multiBusy=false;b.disabled=false;b.textContent='ADD YOUR MATCH TO CART';}
  },true);
  renderGrid(); render();
  Promise.all(Object.entries(baseCatalog).map(async([type,handle])=>{
    try{const r=await fetch(`/products/${handle}.js`);if(!r.ok)throw Error('load');const p=await r.json();baseProducts.set(type,p);}
    catch{baseProducts.set(type,{variants:[]});}
  })).then(()=>render());

  };
  const bootBuildMatchV2 = () => {
    if (document.querySelector('main')) initBuildMatchV2();
    else setTimeout(bootBuildMatchV2, 50);
  };
  bootBuildMatchV2();
  document.addEventListener('DOMContentLoaded', bootBuildMatchV2);
  window.addEventListener('load', bootBuildMatchV2);

  const preferHumanCharm = () => {
    if (location.pathname === '/collections/human-jewelry' && new URLSearchParams(location.search).get('view') !== 'dog-charms') {
      document.querySelectorAll('a[href*="/products/"][href*="-matching-charm"]').forEach((link) => {
        const url = new URL(link.href, location.origin);
        url.searchParams.set('made_for', 'for-you');
        link.href = url.pathname + url.search + url.hash;
      });
    }

    const isCharmProduct = /^\/products\/.+-matching-charm$/.test(location.pathname);
    const wantsHuman = new URLSearchParams(location.search).get('made_for') === 'for-you';
    if (isCharmProduct && wantsHuman) {
      const input = [...document.querySelectorAll('input[type="radio"]')].find((item) => item.value === 'For You');
      if (input && !input.checked) {
        const label = [...document.querySelectorAll('label')].find((item) => item.htmlFor === input.id);
        if (label) label.click();
        else input.click();
      }
    }
  };

  document.addEventListener('DOMContentLoaded', preferHumanCharm);
  window.addEventListener('load', preferHumanCharm);
  new MutationObserver(preferHumanCharm).observe(document.documentElement, { childList: true, subtree: true });
  preferHumanCharm();
/* September 6 human catalog: intentionally leaves dog image URLs untouched. */
(()=>{
  const slugs=new Set('tent cupcake pine-tree mushroom fox wave octopus flower butterfly ice-cream hot-dog acorn pumpkin deer owl snowflake scarf snowman gingerbread tomato-soup you-woof-good pride-heart love-heart key shell'.split(' '));
  const names={'key':'Heart Lock','you-woof-good':'You Feel Like Home','love-heart':'Heart'};
  const originals=new WeakMap();
  const source=slug=>`/cdn/shop/files/human-hook-v2-${slug}.png`;
  const remember=img=>{if(!originals.has(img))originals.set(img,{src:img.getAttribute('src'),srcset:img.getAttribute('srcset')});};
  const setImage=(img,slug)=>{remember(img);const url=source(slug);if(img.getAttribute('src')!==url)img.setAttribute('src',url);if(img.hasAttribute('srcset'))img.removeAttribute('srcset');};
  const restore=img=>{const old=originals.get(img);if(!old)return;img.setAttribute('src',old.src);if(old.srcset)img.setAttribute('srcset',old.srcset);originals.delete(img);};
  function update(){
    const params=new URLSearchParams(location.search);
    if(location.pathname==='/collections/human-jewelry'&&params.get('view')!=='dog-charms'){
      document.querySelectorAll('#product-grid .card-wrapper').forEach(card=>{
        const href=card.querySelector('a[href*="-matching-charm"]')?.getAttribute('href')||'';
        const slug=href.match(/\/products\/([^/?]+)-matching-charm/)?.[1];
        if(!slugs.has(slug))return;
        card.querySelectorAll('.card__media img').forEach(img=>setImage(img,slug));
        if(names[slug])card.querySelectorAll('.card__heading a').forEach(a=>{const name=names[slug]+' Charm';if(a.textContent!==name)a.textContent=name;});
      });
    }
    const slug=location.pathname.match(/\/products\/([^/]+)-matching-charm$/)?.[1];
    if(!slugs.has(slug))return;
    const choice=[...document.querySelectorAll('input[type="radio"]')].find(i=>i.checked&&['For You','For Pup','Matching Pair'].includes(i.value));
    const human=choice?choice.value==='For You':params.get('made_for')==='for-you';
    document.querySelectorAll('media-gallery img,.product-media-modal__content img').forEach(img=>human?setImage(img,slug):restore(img));
  }
  let pending=false;
  const schedule=()=>{if(pending)return;pending=true;requestAnimationFrame(()=>{pending=false;update();});};
  document.addEventListener('change',schedule);
  document.addEventListener('DOMContentLoaded',schedule);
  window.addEventListener('load',schedule);
  new MutationObserver(schedule).observe(document.documentElement,{childList:true,subtree:true});
  schedule();
})();

})();
                                                                                                                                                                                                                                                                                                                                                        
